import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import * as d3 from 'd3';
import { X } from 'lucide-react';
import { SCENARIO_COLORS_BY_NAME } from '../../config/brandColors';
import ModalWrapper from '../ModalWrapper';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import isTouchDevice from '../../utils/isTouchDevice';

const Container = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
  position: relative;
  overflow: hidden;

  html[data-platform='tizen'] & {
    background: #12121a;
  }
`;

const SVGContainer = styled.svg`
  width: 100%;
  height: 100%;
`;

const ZoomControls = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 10;
`;

const ZoomButton = styled.button`
  width: 36px;
  height: 36px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const ZoomLevel = styled.div`
  text-align: center;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.6);
  padding: 4px;
`;


const Tooltip = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 16px 24px;
  max-width: ${props => props.$connectionCount > 6 ? '80vw' : '400px'};
  min-width: 250px;
  pointer-events: none;
  z-index: 100;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.2s ease;
  text-align: center;

  .title {
    font-weight: 600;
    font-size: 1.1rem;
    color: white;
    margin-bottom: 8px;
  }

  .meta {
    font-size: 0.85rem;
    color: #888;
  }

  .connections {
    margin-top: 8px;
    font-size: 0.9rem;
    color: #5B9DD9;
    font-weight: 500;
  }
`;

/** Blobs at or above this radius show the title inside the circle (more readable on dense maps). */
const INSIDE_LABEL_MIN_RADIUS = 24;

/** tspan dy is baseline-to-baseline; must exceed font-size or wrapped titles collide. */
function pivotTitleFontPx(radius, inside) {
  return inside
    ? Math.max(9, Math.min(14, radius * 0.34))
    : Math.max(8, Math.min(11, radius * 0.45));
}

function pivotTitleLineHeight(fontPx, inside) {
  return inside
    ? Math.round(fontPx * 1.35)
    : Math.max(14, Math.round(fontPx * 1.3));
}

/** Word-wrap by characters per line; caps line count and adds ellipsis on the last line when truncated. */
function wrapPivotLabel(text, maxCharsPerLine, maxLines) {
  if (!text) return [];
  const words = text.split(/\s+/).filter(Boolean);
  const allLines = [];
  let currentLine = '';
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length > maxCharsPerLine && currentLine) {
      allLines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) allLines.push(currentLine);
  if (allLines.length <= maxLines) return allLines;
  const out = allLines.slice(0, maxLines);
  const cap = Math.max(4, maxCharsPerLine - 3);
  let last = out[maxLines - 1];
  if (last.length > cap) last = last.slice(0, cap).trimEnd();
  out[maxLines - 1] = `${last}...`;
  return out;
}

/**
 * Force-collide radius treating the visible title (inside or below the blob) as part of the node.
 * External titles use a conservative width/height estimate so neighboring nodes leave room.
 */
function estimatePivotCollisionRadius(d, { maxCharsPerLine, outsideMaxLines }, width, height) {
  const r = d.radius;
  const showTitle = d.isPivot || (d.memberCount || d.size || 0) > 2;
  const hasCount = d.isPivot || (d.memberCount || 1) > 1;

  if (d.usesInsideLabel && showTitle) {
    const insideMaxLines = width < 900 ? 4 : 5;
    const fontPx = pivotTitleFontPx(r, true);
    const lineHeight = pivotTitleLineHeight(fontPx, true);
    const lines = wrapPivotLabel(d.text || '', maxCharsPerLine, insideMaxLines);
    const useLines = lines.slice(0, Math.min(lines.length, insideMaxLines));
    const n = Math.max(1, useLines.length);
    const longest = useLines.length ? Math.max(...useLines.map(l => l.length)) : 8;
    const charW = fontPx * 0.52;
    const pillHalfW =
      0.5 *
      Math.min(
        Math.max(r * 2.1, longest * charW + 24),
        Math.max(width, height) * 0.42
      );
    const firstDy = r * 0.18;
    const textMidY = firstDy + ((n - 1) * lineHeight) / 2;
    const pillHalfH = (n * lineHeight + 16) / 2;
    return Math.hypot(Math.max(r, pillHalfW), Math.max(r, textMidY + pillHalfH)) + 8;
  }
  if (!showTitle) {
    return r + (hasCount ? 24 : 10);
  }

  const fontPx = pivotTitleFontPx(r, false);
  const lineHeight = pivotTitleLineHeight(fontPx, false);
  const gap = 16;
  const lines = wrapPivotLabel(d.text || '', maxCharsPerLine, outsideMaxLines);
  const n = Math.max(1, lines.length);
  const longest = Math.max(1, ...lines.map(l => l.length));
  const textHalfW = longest * fontPx * 0.55;
  const halfW = Math.max(r + 8, textHalfW + 14);
  const extDown = r + gap + n * lineHeight + 12;
  const raw = Math.hypot(halfW, extDown) + 8;
  const cap = Math.min(width, height) * 0.36;
  return Math.min(raw, cap);
}

// Modal components matching ImpactPath NodeModal style
const ModalContent = styled.div`
  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 24px 32px 32px 32px;
  width: 90vw;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  color: #fff;
  position: relative;
`;

const ModalCloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: transparent;
  border: 1px solid #555;
  border-radius: 4px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #888;
  transition: all 0.2s ease;

  &:hover {
    background: #5B9DD9;
    border-color: #5B9DD9;
    color: #fff;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ModalTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #fff;
  padding-right: 50px;
`;

const ModalSection = styled.div`
  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
`;

const ModalSectionTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 0.9rem;
  font-weight: 700;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModalText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #888;
`;

const ConnectionsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ConnectionTag = styled.span`
  display: inline-block;
  background: transparent;
  color: ${props => props.$color || '#5B9DD9'};
  padding: 4px 12px;
  border: 1px solid ${props => props.$color || '#5B9DD9'};
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 500;
`;

const ScenarioDistribution = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 12px;
`;

const ScenarioLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ScenarioLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.85rem;
  color: #fff;

  .color-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .count {
    color: #888;
    font-weight: 600;
  }
`;

const DescriptionText = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.85);
`;

const SentimentGauges = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 12px;
`;

const GaugeContainer = styled.div`
  flex: 1;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 10px 12px;
`;

const GaugeLabel = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 6px;
`;

const GaugeBar = styled.div`
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
`;

const GaugeFill = styled.div`
  height: 100%;
  width: ${props => (props.$value / 5) * 100}%;
  background: ${props => props.$color};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const GaugeValue = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => props.$color};
  float: right;
  margin-top: -18px;
`;

const MetaInfo = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #333;
`;

const MetaItem = styled.div`
  font-size: 0.85rem;
  color: #888;

  span {
    color: #fff;
    font-weight: 500;
  }
`;

// Edge positions: top, right, bottom, left - with more padding to stay on screen
const EDGE_POSITIONS = {
  top: { x: 0.5, y: 0.12, angle: 0 },
  right: { x: 0.88, y: 0.5, angle: 90 },
  bottom: { x: 0.5, y: 0.88, angle: 180 },
  left: { x: 0.12, y: 0.5, angle: 270 }
};

// Gradient direction vectors for each edge (pointing toward that edge)
const EDGE_GRADIENT_VECTORS = {
  top: { x1: '50%', y1: '100%', x2: '50%', y2: '0%' },     // Points upward
  right: { x1: '0%', y1: '50%', x2: '100%', y2: '50%' },   // Points right
  bottom: { x1: '50%', y1: '0%', x2: '50%', y2: '100%' },  // Points downward
  left: { x1: '100%', y1: '50%', x2: '0%', y2: '50%' }     // Points left
};

// Padding for edge calculations
const EDGE_PADDING = 100;

const EDGES = ['top', 'right', 'bottom', 'left'];

// Scenario icon mapping (same as WelcomeScreen)
const SCENARIO_ICONS = {
  'Teknologia': '/assets/icons/technology.svg',
  'Väestö': '/assets/icons/population.svg',
  'Demokratia': '/assets/icons/democracy.svg',
  'Ilmasto': '/assets/icons/climate.svg'
};

// Scenario colors imported from brandColors.js
// Demokratia: yellow, Väestö: pink, Teknologia: green, Ilmasto: blue

function PivotPointsView({
  scenarios = [],
  nodes = [],
  allNodes = [], // All nodes for building parent-child relationships in tree view
  showAllNodes = false,
  viewMode = 'grouped', // 'tree' (Vaikutuspolut), 'grouped', or 'list'
  onNodeClick,
  onNodeHover,
  width = 800,
  height = 600,
  focusedPivotId = null,
  onFocusClear
}) {
  const { t } = useTranslation();
  const svgRef = useRef(null);
  const zoomRef = useRef(null);
  const zoomTransformRef = useRef(d3.zoomIdentity);
  const viewModeRef = useRef(viewMode); // Ref for zoom handler to access current viewMode
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, node: null });
  const [selectedNode, setSelectedNode] = useState(null);
  const [showAll, setShowAll] = useState(showAllNodes);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  const cancelZoomTransitions = (svgSelection) => {
    svgSelection.interrupt();
    svgSelection.interrupt('focus-zoom');
    svgSelection.interrupt('unfocus-zoom');
  };
  const simulationRef = useRef(null);
  const nodesDataRef = useRef([]);

  // Track previous zoom transform for unfocus restore
  const preFocusTransformRef = useRef(null);

  // Sync showAll state with prop when it changes from parent
  React.useEffect(() => {
    setShowAll(showAllNodes);
  }, [showAllNodes]);

  // Keep viewModeRef in sync with viewMode prop
  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  // Scenario colour lookup: prefer DB color on scenarios[], never rely only on the hardcoded name map
  const scenarioById = useMemo(() => {
    const map = {};
    (scenarios || []).forEach(s => { if (s?.id) map[s.id] = s; });
    return map;
  }, [scenarios]);

  const resolveScenarioColor = useCallback((nameOrId) => {
    if (!nameOrId) return null;
    const byId = scenarioById[nameOrId];
    if (byId?.color) return byId.color;
    const lower = String(nameOrId).toLowerCase();
    const byTitle = (scenarios || []).find(s => {
      const title = s.title?.toLowerCase();
      if (!title) return false;
      return title === lower || lower.startsWith(`${title}:`);
    });
    if (byTitle?.color) return byTitle.color;
    return SCENARIO_COLORS_BY_NAME[nameOrId]
      || SCENARIO_COLORS_BY_NAME[lower]
      || null;
  }, [scenarioById, scenarios]);

  // Compute the set of connected node IDs when a pivot is focused
  const connectedNodeIds = useMemo(() => {
    if (!focusedPivotId || viewMode !== 'tree') return null;

    // Find the pivot cluster
    const pivot = nodes.find(n => n.id === focusedPivotId);
    if (!pivot || !pivot.memberIds) return null;

    const connected = new Set();
    connected.add(focusedPivotId); // The cluster overlay itself

    // Build lookup maps from allNodes
    const nodeById = new Map();
    const childrenByParent = new Map();
    allNodes.forEach(n => {
      nodeById.set(n.id, n);
      if (n.parentId) {
        if (!childrenByParent.has(n.parentId)) childrenByParent.set(n.parentId, []);
        childrenByParent.get(n.parentId).push(n.id);
      }
    });
    // Also index from nodes (which includes cluster overlays)
    nodes.forEach(n => {
      if (!nodeById.has(n.id)) nodeById.set(n.id, n);
    });

    // Add all members
    pivot.memberIds.forEach(id => connected.add(id));

    // Trace ancestors of each member up to edge variables
    const variableIds = new Set();
    scenarios.forEach(s => s.variables?.forEach(v => variableIds.add(v.id)));

    pivot.memberIds.forEach(memberId => {
      let current = nodeById.get(memberId);
      const visited = new Set();
      while (current?.parentId && !visited.has(current.parentId)) {
        visited.add(current.parentId);
        connected.add(current.parentId);
        if (variableIds.has(current.parentId)) break; // Stop at edge variable
        current = nodeById.get(current.parentId);
      }
    });

    // Trace descendants of each member (children, grandchildren)
    const addDescendants = (nodeId, depth = 0) => {
      if (depth > 10) return; // Safety limit
      const children = childrenByParent.get(nodeId) || [];
      children.forEach(childId => {
        connected.add(childId);
        addDescendants(childId, depth + 1);
      });
    };
    pivot.memberIds.forEach(id => addDescendants(id));

    return connected;
  }, [focusedPivotId, viewMode, nodes, allNodes, scenarios]);

  // Also track which edge variable IDs are connected (for dimming unconnected variables)
  const connectedVariableIds = useMemo(() => {
    if (!connectedNodeIds) return null;
    const varIds = new Set();
    scenarios.forEach(s => s.variables?.forEach(v => {
      if (connectedNodeIds.has(v.id)) varIds.add(v.id);
    }));
    return varIds;
  }, [connectedNodeIds, scenarios]);

  // Calculate variable positions along each edge
  const variablePositions = useMemo(() => {
    const positions = {};
    const padding = EDGE_PADDING;
    const variablePadding = 45; // Match renderEdges variablePadding
    const edgeLength = {
      top: width - padding * 2,
      bottom: width - padding * 2,
      left: height - padding * 2,
      right: height - padding * 2
    };

    scenarios.forEach((scenario, scenarioIdx) => {
      const edge = EDGES[scenarioIdx % 4];

      scenario.variables.forEach((variable, varIdx) => {
        const varCount = scenario.variables.length;
        const progress = varCount > 1 ? varIdx / (varCount - 1) : 0.5;

        let x, y;
        if (edge === 'top') {
          x = padding + progress * edgeLength.top;
          y = variablePadding;
        } else if (edge === 'bottom') {
          x = padding + progress * edgeLength.bottom;
          y = height - variablePadding;
        } else if (edge === 'left') {
          x = variablePadding;
          y = padding + progress * edgeLength.left;
        } else {
          x = width - variablePadding;
          y = padding + progress * edgeLength.right;
        }

        positions[variable.id] = {
          x,
          y,
          variable,
          scenario,
          edge
        };
      });
    });

    return positions;
  }, [scenarios, width, height]);

  // Process nodes to find pivot points
  const processedNodes = useMemo(() => {
    // Debug: Log variablePositions keys to compare with connectedVariables
    const varPosKeys = Object.keys(variablePositions);
    console.log(`🔑 variablePositions has ${varPosKeys.length} keys:`, varPosKeys.slice(0, 5));

    // ============================================
    // TREE VIEW: Pre-compute sector assignments
    // Each branch from a parent gets its own angular sector
    // ============================================
    let sectorAssignments = {}; // nodeId -> { sectorStart, sectorEnd, sectorCenter }

    if (viewMode === 'tree') {
      // Step 1: Build parent -> children map
      const childrenByParent = {};
      const nodeById = {};
      nodes.forEach(node => {
        nodeById[node.id] = node;
        if (node.parentId) {
          if (!childrenByParent[node.parentId]) {
            childrenByParent[node.parentId] = [];
          }
          childrenByParent[node.parentId].push(node.id);
        }
      });

      // Step 2: Count descendants for each node (for sector width weighting)
      const descendantCount = {};
      const countDescendants = (nodeId) => {
        if (descendantCount[nodeId] !== undefined) return descendantCount[nodeId];
        const children = childrenByParent[nodeId] || [];
        let count = children.length;
        children.forEach(childId => {
          count += countDescendants(childId);
        });
        descendantCount[nodeId] = count;
        return count;
      };
      nodes.forEach(node => countDescendants(node.id));

      // Step 3: Assign sectors recursively from each variable
      // Variables get full sector based on their edge position
      const assignSectors = (parentId, sectorStart, sectorEnd, depth = 0) => {
        const children = childrenByParent[parentId] || [];
        if (children.length === 0) return;

        // Sort children by their ID for deterministic ordering
        children.sort((a, b) => a.localeCompare(b));

        // Calculate total weight (descendants + 1 for each child)
        const totalWeight = children.reduce((sum, childId) => {
          return sum + 1 + (descendantCount[childId] || 0);
        }, 0);

        let currentAngle = sectorStart;
        const sectorRange = sectorEnd - sectorStart;

        children.forEach(childId => {
          const childWeight = 1 + (descendantCount[childId] || 0);
          const childSectorWidth = (childWeight / totalWeight) * sectorRange;
          const childSectorStart = currentAngle;
          const childSectorEnd = currentAngle + childSectorWidth;
          const childSectorCenter = (childSectorStart + childSectorEnd) / 2;

          sectorAssignments[childId] = {
            sectorStart: childSectorStart,
            sectorEnd: childSectorEnd,
            sectorCenter: childSectorCenter,
            parentId: parentId
          };

          // Recursively assign sectors to grandchildren
          assignSectors(childId, childSectorStart, childSectorEnd, depth + 1);

          currentAngle = childSectorEnd;
        });
      };

      // For each variable, assign sectors to its direct children
      // Variables on different edges get different base angles
      // IMPORTANT: Limit fan width to avoid corner crossings between edges
      Object.entries(variablePositions).forEach(([varId, varPos]) => {
        // Base angle pointing toward center from each edge
        let baseAngle;
        switch (varPos.edge) {
          case 'top': baseAngle = Math.PI / 2; break;      // Points down (90°)
          case 'bottom': baseAngle = -Math.PI / 2; break;  // Points up (-90°)
          case 'left': baseAngle = 0; break;               // Points right (0°)
          case 'right': baseAngle = Math.PI; break;        // Points left (180°)
          default: baseAngle = 0;
        }

        // Reduce fan width to 60° to prevent corner overlaps
        // Each edge gets a strict 90° quadrant, but we use only 60° to leave 15° gaps at corners
        const fanWidth = (60 * Math.PI) / 180; // 60 degrees in radians
        const sectorStart = baseAngle - fanWidth / 2;
        const sectorEnd = baseAngle + fanWidth / 2;

        assignSectors(varId, sectorStart, sectorEnd, 0);
      });

      console.log(`🌲 Tree sectors assigned for ${Object.keys(sectorAssignments).length} nodes`);
    }

    const blobScaleFactor = width >= 1800 ? 1.6 : width >= 1000 ? 1.12 : 1;

    // Process nodes for ALL, GROUPED, and TREE views
    const initialNodes = nodes.map((node, idx) => {
      // Connection count: use connectedVariables for "All" view, scenarioNames/scenarioCount for RAG pivot points
      const connectionCount = node.connectedVariables?.length > 0
        ? node.connectedVariables.length
        : (node.scenarioCount || node.scenarioNames?.length || 1);

      // Use isCrossScenario from data if available (true pivot detection based on scenario count)
      // Fall back to connectionCount >= 2 for demo data
      const isPivot = node.isCrossScenario !== undefined ? node.isCrossScenario : connectionCount >= 2;
      const members = node.memberCount || node.size || 1;

      // Calculate TARGET position based on connected variables or scenarioNames
      let targetX = width / 2;
      let targetY = height / 2;

      if (node.connectedVariables?.length > 0) {
        // Method 1: Position based on connected variables (for "All" view)
        const positions = node.connectedVariables
          .map(varId => variablePositions[varId])
          .filter(Boolean);

        // Debug: Log if connectedVariables don't match variablePositions
        if (idx < 5 && positions.length === 0 && node.connectedVariables.length > 0) {
          console.log(`⚠️ Node ${node.id.slice(0, 8)}... has connectedVariables [${node.connectedVariables.slice(0, 2).join(', ')}] but none match variablePositions keys`);
        }

        if (positions.length > 0) {
          targetX = positions.reduce((sum, p) => sum + p.x, 0) / positions.length;
          targetY = positions.reduce((sum, p) => sum + p.y, 0) / positions.length;

          // Pull toward center for pivot points
          if (isPivot) {
            targetX = targetX * 0.6 + (width / 2) * 0.4;
            targetY = targetY * 0.6 + (height / 2) * 0.4;
          }
        } else if (node.scenarioId) {
          // Fallback: If connectedVariables don't resolve, use scenarioId to find a position
          // This happens when the ancestor variable chain doesn't match variablePositions keys
          const scenario = scenarios.find(s => s.id === node.scenarioId);
          if (scenario) {
            const varKey = Object.keys(variablePositions).find(k =>
              variablePositions[k]?.scenario?.id === scenario.id
            );
            if (varKey) {
              const pos = variablePositions[varKey];
              targetX = pos.x * 0.85 + (width / 2) * 0.15;
              targetY = pos.y * 0.85 + (height / 2) * 0.15;
            }
          }
        }
      } else if (node.connectedVariableCounts && Object.keys(node.connectedVariableCounts).length > 0) {
        // Method 2: Position based on connectedVariableCounts (aggregated from cluster members)
        // Each variable has a count = how many cluster members connect to it
        const varCounts = node.connectedVariableCounts;

        let weightedX = 0;
        let weightedY = 0;
        let totalWeight = 0;

        Object.entries(varCounts).forEach(([varId, count]) => {
          const varPos = variablePositions[varId];
          if (varPos) {
            // Weight by count (how many members connect to this variable)
            weightedX += varPos.x * count;
            weightedY += varPos.y * count;
            totalWeight += count;
          }
        });

        if (totalWeight > 0) {
          targetX = weightedX / totalWeight;
          targetY = weightedY / totalWeight;

          // Pull toward center for pivot points (cross-scenario), keep single-scenario closer to their edge
          if (isPivot) {
            // More connections = more toward center
            const centerPull = Math.min(0.5, 0.2 + (connectionCount - 2) * 0.1);
            targetX = targetX * (1 - centerPull) + (width / 2) * centerPull;
            targetY = targetY * (1 - centerPull) + (height / 2) * centerPull;
          } else {
            // Single-scenario nodes stay closer to their edge
            targetX = targetX * 0.85 + (width / 2) * 0.15;
            targetY = targetY * 0.85 + (height / 2) * 0.15;
          }
        }
      } else if (node.scenarioNames?.length > 0) {
        // Method 2b: Fallback - position based on scenario names (for legacy data)
        node.scenarioNames.forEach(name => {
          const scenario = scenarios.find(s =>
            s.title?.toLowerCase() === name.toLowerCase()
          );
          if (scenario) {
            const varKey = Object.keys(variablePositions).find(k =>
              variablePositions[k]?.scenario?.id === scenario.id
            );
            if (varKey) {
              const pos = variablePositions[varKey];
              targetX = pos.x * 0.7 + (width / 2) * 0.3;
              targetY = pos.y * 0.7 + (height / 2) * 0.3;
            }
          }
        });
      }
      // Method 3: Use scenarioId as final fallback for nodes without valid connectedVariables or scenarioNames
      else if (node.scenarioId) {
        const scenario = scenarios.find(s => s.id === node.scenarioId);
        if (scenario) {
          const varKey = Object.keys(variablePositions).find(k =>
            variablePositions[k]?.scenario?.id === scenario.id
          );
          if (varKey) {
            const pos = variablePositions[varKey];
            targetX = pos.x * 0.85 + (width / 2) * 0.15;
            targetY = pos.y * 0.85 + (height / 2) * 0.15;
          }
        }
      }

      // TREE VIEW: Position based on DEPTH and SECTOR from Impact Path
      // Each depth level has a fixed distance from the edge
      // Each branch gets its own angular sector to prevent line crossings
      // NOTE: Cluster overlays are positioned LATER based on member centroids
      if (viewMode === 'tree' && !node.isClusterOverlay) {
        // Only position individual nodes here - clusters are positioned after

        let nodeDepth = node.depth || 1;

        // Find variable positions to determine direction from edge
        const variablePositionsList = [];
        let primaryVarId = null;
        if (node.connectedVariables?.length > 0) {
          node.connectedVariables.forEach(varId => {
            const varPos = variablePositions[varId];
            if (varPos) {
              variablePositionsList.push({ x: varPos.x, y: varPos.y, edge: varPos.edge, id: varId });
              if (!primaryVarId) primaryVarId = varId;
            }
          });
        } else if (node.connectedVariableCounts) {
          Object.keys(node.connectedVariableCounts).forEach(varId => {
            const varPos = variablePositions[varId];
            if (varPos) {
              variablePositionsList.push({ x: varPos.x, y: varPos.y, edge: varPos.edge, id: varId });
              if (!primaryVarId) primaryVarId = varId;
            }
          });
        }

        if (variablePositionsList.length > 0) {
          const centerX = width / 2;
          const centerY = height / 2;

          // FIXED depth bands
          const depthStep = 0.10;
          const baseOffset = 0.08;
          const maxFactor = 0.78;
          const depthFactor = Math.min(baseOffset + (nodeDepth - 1) * depthStep, maxFactor);

          // Get sector assignment for this node (if available)
          const sector = sectorAssignments[node.id];

          if (sector && primaryVarId) {
            // USE SECTOR-BASED POSITIONING
            // Position along the sector's center angle at the correct depth
            const varPos = variablePositions[primaryVarId];
            if (varPos) {
              // Distance from variable toward center based on depth
              const maxDistance = Math.min(width, height) * 0.4; // Max distance from edge
              const distance = maxDistance * depthFactor;

              // Position at sector center angle
              const angle = sector.sectorCenter;
              targetX = varPos.x + Math.cos(angle) * distance;
              targetY = varPos.y + Math.sin(angle) * distance;
            }
          } else {
            // Fallback: use average variable position
            const avgVarX = variablePositionsList.reduce((sum, p) => sum + p.x, 0) / variablePositionsList.length;
            const avgVarY = variablePositionsList.reduce((sum, p) => sum + p.y, 0) / variablePositionsList.length;
            targetX = avgVarX + (centerX - avgVarX) * depthFactor;
            targetY = avgVarY + (centerY - avgVarY) * depthFactor;
          }
        }
      }

      // Use deterministic initial position based on node id hash
      // Small angular spread within the node's sector
      const hash = node.id.split('').reduce((a, b) => ((a << 5) - a) + b.charCodeAt(0), 0);

      // In tree view with sector: spread within the sector bounds
      let offsetX = 0;
      let offsetY = 0;
      if (viewMode === 'tree') {
        const sector = sectorAssignments[node.id];
        if (sector) {
          // Small perpendicular spread within sector (tighter for singleton blobs)
          const spreadAmount = members <= 1 ? 8 : 15;
          const perpAngle = sector.sectorCenter + Math.PI / 2; // Perpendicular to sector direction
          const spreadFraction = ((hash % 100) - 50) / 50; // -1 to 1
          offsetX = Math.cos(perpAngle) * spreadFraction * spreadAmount;
          offsetY = Math.sin(perpAngle) * spreadFraction * spreadAmount;
        } else {
          // Fallback spread for nodes without sector
          offsetX = ((hash % 100) - 50) * 0.15;
          offsetY = (((hash >> 8) % 100) - 50) * 0.15;
        }
      } else {
        // Non-tree view: less jitter for singleton blobs so they stay near their anchor
        const jitter = members <= 1 ? 0.12 : 0.3;
        offsetX = ((hash % 100) - 50) * jitter;
        offsetY = (((hash >> 8) % 100) - 50) * jitter;
      }

      // Scale radius based on memberCount for ALL clusters (display-aware for workshop wall vs laptop)
      let radius;
      if (members > 1) {
        radius = (18 + Math.min(members, 30) * 1.2) * blobScaleFactor;
      } else {
        radius = Math.max(7, 8 * blobScaleFactor);
      }

      const usesInsideLabel = radius >= INSIDE_LABEL_MIN_RADIUS;
      const hasResolvedAnchor = connectionCount > 0;
      const isSingleBlob = members <= 1;

      // Store sector info for debugging and potential future use
      const sector = sectorAssignments[node.id];

      return {
        ...node,
        x: targetX + offsetX,
        y: targetY + offsetY,
        targetX,
        targetY,
        connectionCount,
        isPivot,
        radius,
        usesInsideLabel,
        hasResolvedAnchor,
        isSingleBlob,
        opacity: isPivot ? 1 : 0.7, // Make single-scenario nodes more visible
        sector: sector ? { center: sector.sectorCenter, start: sector.sectorStart, end: sector.sectorEnd } : null
      };
    });

    // ============================================
    // TREE VIEW: Second pass - position cluster overlays
    // Clusters are the MAIN visual element - always positioned toward center
    // They sit at their member centroid, pulled TOWARD center (never backward to edge)
    // ============================================
    if (viewMode === 'tree') {
      // Build map of individual nodes
      const nodeById = {};
      initialNodes.forEach(n => {
        nodeById[n.id] = n;
      });

      const centerX = width / 2;
      const centerY = height / 2;

      // Position each cluster overlay
      initialNodes.forEach(cluster => {
        if (!cluster.isClusterOverlay || !cluster.memberIds) return;

        // Find member nodes
        const memberNodes = cluster.memberIds
          .map(id => nodeById[id])
          .filter(Boolean);

        if (memberNodes.length > 0) {
          // Calculate centroid of member positions
          const memberPositions = memberNodes
            .map(n => ({ x: n.targetX, y: n.targetY }))
            .filter(p => p.x !== undefined && p.y !== undefined);

          if (memberPositions.length > 0) {
            const centroidX = memberPositions.reduce((sum, p) => sum + p.x, 0) / memberPositions.length;
            const centroidY = memberPositions.reduce((sum, p) => sum + p.y, 0) / memberPositions.length;

            // Pull cluster TOWARD center from centroid (never backward to edge)
            // This makes clusters the prominent central elements
            const pullTowardCenter = 50; // pixels toward center
            const dx = centerX - centroidX;
            const dy = centerY - centroidY;
            const dist = Math.sqrt(dx * dx + dy * dy) || 1;

            // Move from centroid toward center
            cluster.targetX = centroidX + (dx / dist) * pullTowardCenter;
            cluster.targetY = centroidY + (dy / dist) * pullTowardCenter;
            cluster.x = cluster.targetX;
            cluster.y = cluster.targetY;
          }
        }
      });

      console.log(`🌲 Positioned ${initialNodes.filter(n => n.isClusterOverlay).length} cluster overlays toward center`);
    }

    return initialNodes;
  }, [nodes, scenarios, variablePositions, width, height, viewMode]);

  // In "Grouped" view, show ALL nodes (both pivots and single-scenario clusters)
  // In "All" view, apply the showAll filter
  const visibleNodes = useMemo(() => {
    // Always show all nodes now - both cross-scenario pivots AND single-scenario clusters
    return processedNodes;
  }, [processedNodes]);

  // Generate gradient definitions for multi-scenario nodes
  const nodeGradients = useMemo(() => {
    const gradients = [];

    visibleNodes.forEach(node => {
      // Collect unique scenarios for this node
      const scenarioInfos = [];
      const seenScenarios = new Set();

      // Method 1: Use connectedVariables to get scenario info (for "All" view nodes)
      if (node.connectedVariables?.length > 0) {
        node.connectedVariables.forEach(varId => {
          const varInfo = variablePositions[varId];
          if (varInfo?.scenario && !seenScenarios.has(varInfo.scenario.id)) {
            seenScenarios.add(varInfo.scenario.id);
            scenarioInfos.push({
              color: varInfo.scenario.color || '#666',
              edge: varInfo.edge
            });
          }
        });
      }
      // Method 2: Use scenarioNames directly (for RAG pivot points in "Grouped" view)
      else if (node.scenarioNames?.length > 0) {
        const edges = ['top', 'right', 'bottom', 'left'];
        node.scenarioNames.forEach((name, idx) => {
          if (!seenScenarios.has(name)) {
            seenScenarios.add(name);
            const color = resolveScenarioColor(name) || '#666';
            scenarioInfos.push({
              color,
              edge: edges[idx % edges.length] // Distribute edges for gradient direction
            });
          }
        });
      }

      if (scenarioInfos.length < 2) return; // Single scenario, no gradient needed

      // Create gradient ID
      const gradientId = `node-gradient-${node.id}`;

      // For radial gradient with directional color stops based on edge positions
      // We'll use a radial gradient that fades colors toward their edge positions
      gradients.push({
        id: gradientId,
        nodeId: node.id,
        scenarios: scenarioInfos
      });
    });

    return gradients;
  }, [visibleNodes, variablePositions, resolveScenarioColor]);

  // Build tree connections for tree view mode
  // Two types of connections:
  // 1. Tree connections: actual parent-child relationships between individual nodes
  // 2. Cluster connections: dashed lines from cluster overlays to their member nodes
  const { treeConnections, clusterConnections } = useMemo(() => {
    if (viewMode !== 'tree') return { treeConnections: [], clusterConnections: [] };

    // Separate individual nodes from cluster overlays
    const individualNodes = visibleNodes.filter(n => !n.isClusterOverlay);
    const clusterOverlays = visibleNodes.filter(n => n.isClusterOverlay);

    // Build set of visible individual node IDs
    const visibleNodeIds = new Set(individualNodes.map(n => n.id));

    // 1. Build TREE connections (parent-child between individual nodes)
    const treeConns = [];
    individualNodes.forEach(node => {
      if (!node.parentId) return;

      // Only create tree connection if parent is also a visible individual node
      if (visibleNodeIds.has(node.parentId)) {
        treeConns.push({
          sourceId: node.id,
          targetId: node.parentId,
          strength: 1
        });
      }
    });

    // 2. Build CLUSTER connections (cluster overlay -> member nodes)
    // Also build a reverse lookup: node -> cluster (for fallback connections)
    const nodeToCluster = new Map();
    clusterOverlays.forEach(cluster => {
      (cluster.memberIds || []).forEach(memberId => {
        nodeToCluster.set(memberId, cluster.id);
      });
    });

    const clusterConns = [];
    clusterOverlays.forEach(cluster => {
      if (!cluster.memberIds || cluster.memberIds.length === 0) {
        console.log(`⚠️ Cluster ${cluster.id.slice(0, 8)}... has no memberIds`);
        return;
      }

      let connectedCount = 0;
      cluster.memberIds.forEach(memberId => {
        if (visibleNodeIds.has(memberId)) {
          clusterConns.push({
            sourceId: cluster.id,
            targetId: memberId,
            strength: 1
          });
          connectedCount++;
        }
      });

      // FALLBACK: If no direct member matches, try matching via clusterId on individual nodes
      if (connectedCount === 0) {
        // Look for individual nodes that have this cluster's ID in their clusterId field
        individualNodes.forEach(node => {
          if (node.clusterId === cluster.id) {
            clusterConns.push({
              sourceId: cluster.id,
              targetId: node.id,
              strength: 1
            });
            connectedCount++;
          }
        });

        if (connectedCount === 0) {
          console.log(`⚠️ Cluster "${cluster.text?.slice(0, 30) || cluster.centroidText?.slice(0, 30)}..." has ${cluster.memberIds.length} memberIds but NONE match visible nodes or clusterId. Sample memberIds:`, cluster.memberIds.slice(0, 3));
        } else {
          console.log(`✓ Cluster "${cluster.text?.slice(0, 30)}..." connected via clusterId fallback: ${connectedCount} connections`);
        }
      }
    });

    console.log(`🌲 Tree view: ${treeConns.length} tree connections, ${clusterConns.length} cluster connections`);
    return { treeConnections: treeConns, clusterConnections: clusterConns };
  }, [viewMode, visibleNodes]);


  // D3 force simulation - only recreate when nodes change, NOT on hover
  useEffect(() => {
    if (!svgRef.current || visibleNodes.length === 0) return;

    const svg = d3.select(svgRef.current);

    // Stop any existing simulation
    if (simulationRef.current) {
      simulationRef.current.stop();
    }

    // Clear previous elements (includes legacy `.node-group` from older builds)
    svg.select('.zoomable-content .nodes-layer').selectAll('*').remove();
    svg.select('.zoomable-content g.pivot-labels-overlay').remove();
    svg.selectAll('.connection-line').remove();
    svg.selectAll('.tree-connection').remove();
    svg.selectAll('.cluster-connection').remove();

    // Create a copy of nodes data for D3 to mutate
    const nodesData = visibleNodes.map(n => ({ ...n }));
    nodesDataRef.current = nodesData;

    // Debug: In tree mode, log what variablePositions we have vs what parentIds exist
    if (viewMode === 'tree') {
      const varPosIds = Object.keys(variablePositions);
      const allParentIds = new Set(allNodes.map(n => n.parentId).filter(Boolean));
      const matchingParentIds = [...allParentIds].filter(pid => varPosIds.includes(pid));
      console.log(`🌲 Tree debug: ${varPosIds.length} variablePos keys, ${allParentIds.size} unique parentIds in allNodes, ${matchingParentIds.length} matching`);
      if (matchingParentIds.length === 0 && allParentIds.size > 0) {
        console.log(`🌲 Sample variablePos keys:`, varPosIds.slice(0, 3));
        console.log(`🌲 Sample parentIds:`, [...allParentIds].slice(0, 3));
      }
    }

    // Create connection lines data (to edge Variables)
    // In TREE view: only show variable connections for INDIVIDUAL nodes that DIRECTLY connect to variables
    // Cluster overlays NEVER have variable connections - they only have dashed lines to members
    const connections = [];
    nodesData.forEach(node => {
      // In tree view: special handling for variable connections
      if (viewMode === 'tree') {
        // CLUSTER OVERLAYS: Never show variable connections - they only connect to members via dashed lines
        if (node.isClusterOverlay) {
          return; // Skip entirely - clusters only have dashed member connections
        }

        // INDIVIDUAL NODES: Only show variable connection if directly connected to a Variable
        const varPosIds = Object.keys(variablePositions);
        const hasDirectVariableConnection = node.parentId && varPosIds.includes(node.parentId);

        // Check if this node has any tree connections (either as source or target)
        const hasTreeConnection = treeConnections.some(
          conn => conn.sourceId === node.id || conn.targetId === node.id
        );

        // Skip variable connection ONLY if:
        // 1. Node doesn't have direct variable connection, AND
        // 2. Node HAS tree connections (so it won't be floating)
        if (!hasDirectVariableConnection && hasTreeConnection) {
          return; // Skip - this node connects via tree connections
        }
        // Otherwise: either it has direct variable connection, or it needs fallback variable connection
      }

      // Method 1: Use connectedVariables (for "All" view nodes)
      if (node.connectedVariables?.length > 0) {
        let hasValidConnection = false;
        node.connectedVariables.forEach(varId => {
          const varPos = variablePositions[varId];
          if (varPos) {
            hasValidConnection = true;
            connections.push({
              source: node,
              target: varPos,
              nodeId: node.id,
              varId,
              scenarioColor: varPos.scenario?.color
            });
          }
        });

        // Fallback: If connectedVariables didn't resolve, use scenarioId to draw connection
        if (!hasValidConnection && node.scenarioId) {
          const scenario = scenarios.find(s => s.id === node.scenarioId);
          if (scenario) {
            const varKey = Object.keys(variablePositions).find(k =>
              variablePositions[k]?.scenario?.id === scenario.id
            );
            if (varKey) {
              const varPos = variablePositions[varKey];
              connections.push({
                source: node,
                target: varPos,
                nodeId: node.id,
                varId: varKey,
                scenarioColor: scenario.color || varPos.scenario?.color
              });
            }
          }
        }
      }
      // Method 2: Use connectedVariableCounts to draw ONE line per unique variable (for RAG clusters)
      // A cluster with 14 members connecting to 8 unique variables = 8 lines
      else if (node.connectedVariableCounts && Object.keys(node.connectedVariableCounts).length > 0) {
        const variableCounts = node.connectedVariableCounts;
        const uniqueVarIds = Object.keys(variableCounts);
        const totalUniqueVars = uniqueVarIds.length;

        uniqueVarIds.forEach((varId, idx) => {
          const varPos = variablePositions[varId];
          if (varPos) {
            const color = varPos.scenario?.color || '#666';
            connections.push({
              source: node,
              target: varPos,
              nodeId: node.id,
              varId: varId,
              scenarioColor: color,
              lineIndex: idx, // For slight spread offset
              totalLines: totalUniqueVars, // Total lines from this node
              memberCount: variableCounts[varId] // How many members connect to this var (for line thickness)
            });
          }
        });
      }
      // Fallback: single line per scenario if no distribution data
      else if (node.scenarioNames?.length > 0) {
        node.scenarioNames.forEach(name => {
          const scenario = scenarios.find(s =>
            s.title?.toLowerCase() === name.toLowerCase()
          );
          if (scenario) {
            const varKey = Object.keys(variablePositions).find(k =>
              variablePositions[k]?.scenario?.id === scenario.id
            );
            if (varKey) {
              const varPos = variablePositions[varKey];
              connections.push({
                source: node,
                target: varPos,
                nodeId: node.id,
                varId: varKey,
                scenarioColor: scenario.color || SCENARIO_COLORS_BY_NAME[name] || '#666'
              });
            }
          }
        });
      }
      // Method 3: Use scenarioId as fallback for nodes without connectedVariables or scenarioNames
      else if (node.scenarioId) {
        const scenario = scenarios.find(s => s.id === node.scenarioId);
        if (scenario) {
          const varKey = Object.keys(variablePositions).find(k =>
            variablePositions[k]?.scenario?.id === scenario.id
          );
          if (varKey) {
            const varPos = variablePositions[varKey];
            connections.push({
              source: node,
              target: varPos,
              nodeId: node.id,
              varId: varKey,
              scenarioColor: scenario.color || varPos.scenario?.color
            });
          }
        }
      }
    });

    // Draw connection lines inside zoomable-content
    const zoomableContent = svg.select('.zoomable-content');
    const connectionGroup = zoomableContent.select('.connections-layer');

    // In tree view: use straight lines with uniform width
    // In other views: use curved lines with variable width
    if (viewMode === 'tree') {
      // Tree view: straight lines, uniform width
      connectionGroup.selectAll('.connection-line')
        .data(connections)
        .enter()
        .append('line')
        .attr('class', 'connection-line')
        .attr('data-node-id', d => d.nodeId)
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y)
        .attr('stroke', d => d.scenarioColor || variablePositions[d.varId]?.scenario?.color || 'rgba(255, 255, 255, 0.2)')
        .attr('stroke-width', 1.5)
        .attr('stroke-opacity', 0.6);
    } else {
      // Other views: curved lines with variable width
      connectionGroup.selectAll('.connection-line')
        .data(connections)
        .enter()
        .append('path')
        .attr('class', 'connection-line')
        .attr('data-node-id', d => d.nodeId)
        .attr('d', d => {
          const midX = (d.source.x + d.target.x) / 2;
          const midY = (d.source.y + d.target.y) / 2;
          const dx = d.target.x - d.source.x;
          const dy = d.target.y - d.source.y;
          const len = Math.sqrt(dx * dx + dy * dy) || 1;

          // Spread multiple lines perpendicular to line direction
          // lineIndex 0,1,2... with totalLines = N, spread them out
          let spreadOffset = 0;
          if (d.lineIndex !== undefined && d.totalLines > 1) {
            // Center the lines: -((N-1)/2), ..., 0, ..., ((N-1)/2)
            const centerIndex = (d.totalLines - 1) / 2;
            spreadOffset = (d.lineIndex - centerIndex) * 4; // 4px spacing between lines
          }

          // Perpendicular direction (normalized)
          const perpX = -dy / len;
          const perpY = dx / len;

          // Apply spread offset to control point
          const cx = midX - dy * 0.1 + perpX * spreadOffset;
          const cy = midY + dx * 0.1 + perpY * spreadOffset;

          return `M ${d.source.x} ${d.source.y} Q ${cx} ${cy} ${d.target.x} ${d.target.y}`;
        })
        .attr('stroke', d => {
          // Use pre-computed scenarioColor, or fall back to lookup
          return d.scenarioColor || variablePositions[d.varId]?.scenario?.color || 'rgba(255, 255, 255, 0.2)';
        })
        .attr('stroke-width', d => {
          // If memberCount is set (cluster line), use it to vary thickness
          // More members connecting to this variable = thicker line
          if (d.memberCount && d.memberCount > 1) {
            return Math.min(1 + d.memberCount * 0.5, 5); // Scale from 1.5 to max 5
          }
          return d.source.isPivot ? 2 : 1;
        })
        .attr('stroke-opacity', d => d.source.isPivot ? 0.4 : 0.15)
        .attr('fill', 'none');
    }

    // In Tree view: Draw cluster-to-cluster tree connections
    if (viewMode === 'tree' && treeConnections.length > 0) {
      // Build a map of node/cluster IDs to their D3 data objects
      const nodeDataById = new Map();
      nodesData.forEach(n => nodeDataById.set(n.id, n));

      // Filter to tree connections where both source and target exist in visible nodes
      const validTreeConns = treeConnections.filter(conn =>
        nodeDataById.has(conn.sourceId) && nodeDataById.has(conn.targetId)
      );

      console.log(`🌲 Drawing ${validTreeConns.length} cluster-to-cluster tree connections`);

      // Helper to get color for a node/cluster based on its scenario
      const getNodeColor = (node) => {
        // Use connectedVariableCounts to find dominant scenario
        if (node.connectedVariableCounts) {
          const varIds = Object.keys(node.connectedVariableCounts);
          if (varIds.length > 0) {
            // Get color from first (or most connected) variable
            let maxCount = 0;
            let dominantVarId = varIds[0];
            Object.entries(node.connectedVariableCounts).forEach(([varId, count]) => {
              if (count > maxCount) {
                maxCount = count;
                dominantVarId = varId;
              }
            });
            const varPos = variablePositions[dominantVarId];
            if (varPos?.scenario?.color) return varPos.scenario.color;
          }
        }
        // Fallback to connectedVariables
        if (node.connectedVariables?.length > 0) {
          const varPos = variablePositions[node.connectedVariables[0]];
          if (varPos?.scenario?.color) return varPos.scenario.color;
        }
        // Fallback to scenarioId (DB color)
        if (node.scenarioId && scenarioById[node.scenarioId]?.color) {
          return scenarioById[node.scenarioId].color;
        }
        // Fallback to scenarioNames
        if (node.scenarioNames?.length > 0) {
          return resolveScenarioColor(node.scenarioNames[0]) || '#888';
        }
        return '#888';
      };

      // Draw TREE connections (solid lines for parent-child)
      connectionGroup.selectAll('.tree-connection')
        .data(validTreeConns)
        .enter()
        .append('line')
        .attr('class', 'tree-connection')
        .attr('x1', d => {
          const sourceNode = nodeDataById.get(d.sourceId);
          return sourceNode ? sourceNode.x : 0;
        })
        .attr('y1', d => {
          const sourceNode = nodeDataById.get(d.sourceId);
          return sourceNode ? sourceNode.y : 0;
        })
        .attr('x2', d => {
          const targetNode = nodeDataById.get(d.targetId);
          return targetNode ? targetNode.x : 0;
        })
        .attr('y2', d => {
          const targetNode = nodeDataById.get(d.targetId);
          return targetNode ? targetNode.y : 0;
        })
        .attr('stroke', d => {
          // Color based on the SOURCE node (the child/deeper one)
          const sourceNode = nodeDataById.get(d.sourceId);
          return sourceNode ? getNodeColor(sourceNode) : '#888';
        })
        .attr('stroke-width', 1.5)
        .attr('stroke-opacity', 0.6);

      // Draw CLUSTER connections (dashed lines from clusters to member nodes)
      const validClusterConns = clusterConnections.filter(conn =>
        nodeDataById.has(conn.sourceId) && nodeDataById.has(conn.targetId)
      );

      console.log(`🌲 Drawing ${validClusterConns.length} cluster-to-member connections`);

      connectionGroup.selectAll('.cluster-connection')
        .data(validClusterConns)
        .enter()
        .append('line')
        .attr('class', 'cluster-connection')
        .attr('x1', d => nodeDataById.get(d.sourceId)?.x || 0)
        .attr('y1', d => nodeDataById.get(d.sourceId)?.y || 0)
        .attr('x2', d => nodeDataById.get(d.targetId)?.x || 0)
        .attr('y2', d => nodeDataById.get(d.targetId)?.y || 0)
        .attr('stroke', d => {
          // Use TARGET node (member) color, not cluster color
          // Each dashed line should match the member node's scenario
          const memberNode = nodeDataById.get(d.targetId);
          return memberNode ? getNodeColor(memberNode) : '#888';
        })
        .attr('stroke-width', 1)
        .attr('stroke-opacity', 0.4)
        .attr('stroke-dasharray', '4,4'); // Dashed line
    }

    const LAYOUT_MAX_CHARS = width >= 1800 ? 22 : width >= 900 ? 18 : 14;
    const LAYOUT_OUT_LINES = width < 640 ? 2 : 3;
    nodesData.forEach(d => {
      d._collisionR = estimatePivotCollisionRadius(d, {
        maxCharsPerLine: LAYOUT_MAX_CHARS,
        outsideMaxLines: LAYOUT_OUT_LINES,
      }, width, height);
    });

    // Keep node centers away from edge scenario/variable labels (they sit in a ~90–120px band).
    const inset = {
      top: Math.max(96, Math.min(138, height * 0.165)),
      bottom: Math.max(88, Math.min(128, height * 0.145)),
      left: Math.max(88, Math.min(128, width * 0.12)),
      right: Math.max(88, Math.min(128, width * 0.12)),
    };

    // Create force simulation - very different settings for tree view
    const isTreeView = viewMode === 'tree';
    const graphIsSingleton = nodesData.length === 1;

    if (isTreeView) {
      inset.top = Math.max(78, inset.top - 18);
      inset.bottom = Math.max(78, inset.bottom - 10);
    }

    const chargeStrength = (d) => {
      if (isTreeView) return -30;
      const single = graphIsSingleton || d.isSingleBlob;
      if (single && d.hasResolvedAnchor) return -16;
      if (single) return -24;
      return -50;
    };

    const collideRadius = (d) => {
      const r = d._collisionR ?? d.radius + 12;
      if (isTreeView) return Math.max(d.radius + 8, r * 0.92);
      return r;
    };

    const axisStrength = (d) => {
      if (isTreeView) return 0.6;
      const single = graphIsSingleton || d.isSingleBlob;
      if (single && d.hasResolvedAnchor) return 0.52;
      if (single) return 0.42;
      return 0.3;
    };

    const simulation = d3.forceSimulation(nodesData)
      .force('charge', d3.forceManyBody().strength(chargeStrength))
      .force('collision', d3.forceCollide().radius(collideRadius).iterations(14))
      .force('x', d3.forceX(d => d.targetX).strength(axisStrength))
      .force('y', d3.forceY(d => d.targetY).strength(axisStrength))
      // Boundary constraints
      .force('boundX', () => {
        nodesData.forEach(d => {
          d.x = Math.max(inset.left, Math.min(width - inset.right, d.x));
        });
      })
      .force('boundY', () => {
        nodesData.forEach(d => {
          d.y = Math.max(inset.top, Math.min(height - inset.bottom, d.y));
        });
      })
      .alphaDecay(isTreeView ? 0.02 : graphIsSingleton ? 0.035 : 0.03)
      .velocityDecay(isTreeView ? 0.5 : graphIsSingleton ? 0.52 : 0.4);

    // In tree view: add WEAK link forces for TREE connections only
    // Cluster connections are purely visual - they don't affect the force layout
    if (isTreeView) {
      // Tree links (parent-child) - WEAK, just helps adjacent nodes stay close
      // NO cluster links - clusters are positioned based on member positions, not forces
      if (treeConnections.length > 0) {
        const treeLinks = treeConnections
          .map(conn => {
            const source = nodesData.find(n => n.id === conn.sourceId);
            const target = nodesData.find(n => n.id === conn.targetId);
            if (source && target) {
              return { source, target };
            }
            return null;
          })
          .filter(Boolean);

        if (treeLinks.length > 0) {
          simulation.force('treeLinks', d3.forceLink(treeLinks)
            .id(d => d.id)
            .distance(80)   // Target distance between linked nodes
            .strength(0.1)  // Very weak - position forces dominate
          );
        }
      }
      // NOTE: Cluster links are NOT added to the simulation
      // Cluster overlays will be positioned at the centroid of their members
    }

    simulationRef.current = simulation;

    // Blobs under connection lines' plane... connections first in DOM, then blobs, then labels overlay:
    // labels are a sibling AFTER nodes-layer so every title stacks above links + every blob.
    const nodeGroup = zoomableContent.select('.nodes-layer');
    const blobsLayer = nodeGroup.append('g').attr('class', 'pivot-blobs-layer');
    const labelsLayer = zoomableContent.append('g').attr('class', 'pivot-labels-overlay');

    // Smaller nodes' labels drawn last so they win when titles overlap
    const labelDrawData = [...nodesData].sort((a, b) => {
      if (a.radius !== b.radius) return a.radius - b.radius;
      return String(a.id).localeCompare(String(b.id));
    });

    const blobElements = blobsLayer
      .selectAll('g.pivot-blob-g')
      .data(nodesData, d => d.id)
      .enter()
      .append('g')
      .attr('class', 'pivot-blob-g')
      .style('cursor', 'pointer');

    // Helper to check if node needs diamond gradient (3-4 scenarios)
    const needsDiamondGradient = (d) => {
      const gradient = nodeGradients.find(g => g.nodeId === d.id);
      return gradient && gradient.scenarios.length >= 3;
    };

    // Helper to get gradient ID for a node (2-scenario linear gradients)
    const getNodeFill = (d) => {
      // Check if this node has a gradient defined
      const gradient = nodeGradients.find(g => g.nodeId === d.id);
      if (gradient && gradient.scenarios.length >= 2) {
        return `url(#${gradient.id})`;
      }

      // For single-connection nodes, use scenario color
      if (d.connectedVariables?.length > 0) {
        const varId = d.connectedVariables[0];
        const varInfo = variablePositions[varId];
        if (varInfo?.scenario?.color) {
          return varInfo.scenario.color;
        }
      }
      // Prefer DB color via scenarioId
      if (d.scenarioId && scenarioById[d.scenarioId]?.color) {
        return scenarioById[d.scenarioId].color;
      }
      // For nodes with scenarioNames (RAG pivot points), resolve via scenarios[] then name map
      if (d.scenarioNames?.length > 0) {
        const color = resolveScenarioColor(d.scenarioNames[0]);
        if (color) return color;
      }
      return '#666'; // Gray fallback
    };

    // For nodes with 3-4 scenarios, render 4 quadrant circles with clip paths
    blobElements.filter(needsDiamondGradient).each(function(d) {
      const group = d3.select(this);
      const gradient = nodeGradients.find(g => g.nodeId === d.id);
      const gradientId = gradient.id;

      // Create a group for the 4 quadrants with blur for soft transitions
      const quadrantGroup = group.append('g')
        .attr('class', 'diamond-gradient')
        .style('filter', `url(#${gradientId}-blur)`);

      // Define clip paths in local coordinates (circle centered at 0,0)
      // Top quadrant - extend slightly past 50% for overlap/blending
      quadrantGroup.append('circle')
        .attr('r', d.radius)
        .attr('fill', `url(#${gradientId}-top)`)
        .style('clip-path', `polygon(50% 55%, -5% -5%, 105% -5%)`);

      // Right quadrant
      quadrantGroup.append('circle')
        .attr('r', d.radius)
        .attr('fill', `url(#${gradientId}-right)`)
        .style('clip-path', `polygon(45% 50%, 105% -5%, 105% 105%)`);

      // Bottom quadrant
      quadrantGroup.append('circle')
        .attr('r', d.radius)
        .attr('fill', `url(#${gradientId}-bottom)`)
        .style('clip-path', `polygon(50% 45%, 105% 105%, -5% 105%)`);

      // Left quadrant
      quadrantGroup.append('circle')
        .attr('r', d.radius)
        .attr('fill', `url(#${gradientId}-left)`)
        .style('clip-path', `polygon(55% 50%, -5% 105%, -5% -5%)`);

      // Add a clipping circle to keep it round after blur
      group.append('clipPath')
        .attr('id', `${gradientId}-circle-clip`)
        .append('circle')
        .attr('r', d.radius);
    });

    // Node circles (for non-diamond nodes)
    blobElements.filter(d => !needsDiamondGradient(d)).append('circle')
      .attr('r', d => d.radius)
      .attr('fill', getNodeFill)
      .attr('fill-opacity', d => d.isPivot || d.isClusterOverlay ? 1 : 0.85)
      .attr('stroke', d => d.isPivot ? 'rgba(255, 255, 255, 0.5)' : 'none')
      .attr('stroke-width', 2)
      .style('filter', d => d.isPivot ? 'drop-shadow(0 0 8px rgba(76, 175, 80, 0.5))' : 'none');

    // Add stroke circle on top for diamond gradient nodes
    blobElements.filter(needsDiamondGradient).append('circle')
      .attr('r', d => d.radius)
      .attr('fill', 'none')
      .attr('stroke', 'rgba(255, 255, 255, 0.5)')
      .attr('stroke-width', 2)
      .style('filter', 'drop-shadow(0 0 8px rgba(76, 175, 80, 0.5))');

    const labelElements = labelsLayer
      .selectAll('g.pivot-label-g')
      .data(labelDrawData, d => d.id)
      .enter()
      .append('g')
      .attr('class', 'pivot-label-g')
      .attr('data-node-id', d => d.id)
      .style('pointer-events', 'none');

    // Node labels for pivots and clusters with >2 votes — inside large blobs, below small ones
    const maxCharsPerLine = LAYOUT_MAX_CHARS;
    const outsideMaxLines = LAYOUT_OUT_LINES;
    const insideMaxLines = width < 900 ? 4 : 5;

    labelElements.filter(d => d.isPivot || (d.memberCount || d.size || 0) > 2)
      .each(function(d) {
        const group = d3.select(this);
        const inside = d.usesInsideLabel;
        const textHost = group.append('g').attr('class', 'pivot-title-stack');

        const maxLines = inside ? insideMaxLines : outsideMaxLines;
        const lines = wrapPivotLabel(d.text || '', maxCharsPerLine, maxLines);
        const fontPx = pivotTitleFontPx(d.radius, inside);
        const lineHeight = pivotTitleLineHeight(fontPx, inside);

        const useLinesInside = inside
          ? lines.slice(0, Math.min(lines.length, insideMaxLines))
          : [];
        const firstDyInside = d.radius * 0.18;

        if (inside && useLinesInside.length > 0) {
          const n = useLinesInside.length;
          const longest = Math.max(...useLinesInside.map(l => l.length));
          const charW = fontPx * 0.52;
          const pillW = Math.min(
            Math.max(d.radius * 2.1, longest * charW + 24),
            Math.max(width, height) * 0.42
          );
          const pillPadY = 8;
          const pillH = n * lineHeight + pillPadY * 2;
          const rx = Math.min(pillH / 2, 18);
          const textMidY = firstDyInside + ((n - 1) * lineHeight) / 2;
          textHost
            .append('rect')
            .attr('class', 'pivot-title-pill')
            .attr('x', -pillW / 2)
            .attr('y', textMidY - pillH / 2)
            .attr('width', pillW)
            .attr('height', pillH)
            .attr('rx', rx)
            .attr('ry', rx)
            .attr('fill', 'rgba(10, 10, 22, 0.5)')
            .attr('stroke', 'rgba(255, 255, 255, 0.16)')
            .attr('stroke-width', 1);
        }

        const textGroup = textHost.append('text')
          .attr('text-anchor', 'middle')
          .attr('fill', 'white')
          .attr('font-weight', '600');

        if (!inside) {
          textGroup
            .attr('stroke', '#0a0a14')
            .attr('stroke-width', 2.5)
            .attr('paint-order', 'stroke fill');
        }

        textGroup.attr('font-size', fontPx + 'px');

        if (inside) {
          useLinesInside.forEach((line, i) => {
            textGroup.append('tspan')
              .attr('x', 0)
              .attr('dy', i === 0 ? firstDyInside : lineHeight)
              .text(line);
          });
        } else {
          const startY = d.radius + 16;
          lines.forEach((line, i) => {
            textGroup.append('tspan')
              .attr('x', 0)
              .attr('dy', i === 0 ? startY : lineHeight)
              .text(line);
          });
        }
      });

    // Member count badge — keep high when title is inside the blob so it does not overlap the title block
    labelElements.filter(d => d.isPivot || d.memberCount > 1)
      .append('text')
      .attr('class', 'pivot-member-count')
      .attr('text-anchor', 'middle')
      .attr('dy', d => {
        const showTitle = d.isPivot || (d.memberCount || d.size || 0) > 2;
        if (d.usesInsideLabel && showTitle) return -d.radius * 0.48;
        return d.radius * 0.15;
      })
      .attr('fill', 'white')
      .attr('font-size', d => {
        const showTitle = d.isPivot || (d.memberCount || d.size || 0) > 2;
        if (d.usesInsideLabel && showTitle) {
          return Math.max(11, Math.min(16, d.radius * 0.42)) + 'px';
        }
        return Math.max(12, d.radius * 0.5) + 'px';
      })
      .attr('font-weight', '700')
      .attr('stroke', '#0a0a14')
      .attr('stroke-width', 1.8)
      .attr('paint-order', 'stroke fill')
      .text(d => d.memberCount || d.size || 1);

    // Event handlers (hit target = blob only)
    blobElements
      .on('mouseenter', (event, d) => {
        setHoveredNode(d.id);
        setTooltip({
          visible: true,
          x: event.pageX,
          y: event.pageY - 10,
          node: d
        });
        onNodeHover?.(d);
      })
      .on('mouseleave', () => {
        setHoveredNode(null);
        setTooltip({ visible: false, x: 0, y: 0, node: null });
        onNodeHover?.(null);
      })
      .on('click', (event, d) => {
        // Only open internal modal if no external click handler is provided
        if (!onNodeClick) {
          setSelectedNode(d);
        }
        onNodeClick?.(d);
      });

    // Update positions on tick - use raw coordinates (group transform handles zoom)
    simulation.on('tick', () => {
      const t = d => `translate(${d.x}, ${d.y})`;
      blobElements.attr('transform', t);
      labelElements.attr('transform', t);

      // For connection lines: source uses raw coords, but target needs inverse transform
      // so it appears at the fixed edge position when the group is transformed
      const transform = zoomTransformRef.current;

      if (viewMode === 'tree') {
        // Tree view: straight lines (line elements with x1/y1/x2/y2)
        connectionGroup.selectAll('.connection-line')
          .attr('x1', d => d.source.x)
          .attr('y1', d => d.source.y)
          .attr('x2', d => transform.invertX(d.target.x))
          .attr('y2', d => transform.invertY(d.target.y));

        // Update tree connection lines between nodes
        const nodeDataById = new Map();
        nodesData.forEach(n => nodeDataById.set(n.id, n));

        connectionGroup.selectAll('.tree-connection')
          .attr('x1', d => nodeDataById.get(d.sourceId)?.x || 0)
          .attr('y1', d => nodeDataById.get(d.sourceId)?.y || 0)
          .attr('x2', d => nodeDataById.get(d.targetId)?.x || 0)
          .attr('y2', d => nodeDataById.get(d.targetId)?.y || 0);

        // Update cluster connection lines (dashed lines to members)
        connectionGroup.selectAll('.cluster-connection')
          .attr('x1', d => nodeDataById.get(d.sourceId)?.x || 0)
          .attr('y1', d => nodeDataById.get(d.sourceId)?.y || 0)
          .attr('x2', d => nodeDataById.get(d.targetId)?.x || 0)
          .attr('y2', d => nodeDataById.get(d.targetId)?.y || 0);
      } else {
        // Other views: curved lines (path elements with d attribute)
        connectionGroup.selectAll('.connection-line')
          .attr('d', d => {
            // Source (node) uses raw simulation coordinates
            const sourceX = d.source.x;
            const sourceY = d.source.y;
            // Target (edge) needs inverse transform to appear at fixed screen position
            const targetX = transform.invertX(d.target.x);
            const targetY = transform.invertY(d.target.y);

            const midX = (sourceX + targetX) / 2;
            const midY = (sourceY + targetY) / 2;
            const dx = targetX - sourceX;
            const dy = targetY - sourceY;
            const len = Math.sqrt(dx * dx + dy * dy) || 1;

            // Spread multiple lines perpendicular to line direction
            let spreadOffset = 0;
            if (d.lineIndex !== undefined && d.totalLines > 1) {
              const centerIndex = (d.totalLines - 1) / 2;
              spreadOffset = (d.lineIndex - centerIndex) * 4;
            }

            const perpX = -dy / len;
            const perpY = dx / len;

            const cx = midX - dy * 0.1 + perpX * spreadOffset;
            const cy = midY + dx * 0.1 + perpY * spreadOffset;
            return `M ${sourceX} ${sourceY} Q ${cx} ${cy} ${targetX} ${targetY}`;
          });
      }
    });

    return () => {
      simulation.stop();
      // Clean up all D3 elements on unmount
      svg.select('.zoomable-content .nodes-layer').selectAll('*').remove();
      svg.select('.zoomable-content g.pivot-labels-overlay').remove();
      svg.selectAll('.connection-line').remove();
      svg.selectAll('.tree-connection').remove();
      svg.selectAll('.cluster-connection').remove();
    };
  }, [visibleNodes, variablePositions, nodeGradients, scenarios, scenarioById, resolveScenarioColor, width, height, onNodeClick, onNodeHover, viewMode, treeConnections, clusterConnections]);

  // Separate effect for hover styling - does NOT restart simulation
  // Must respect focusedPivotId: when focused, only connected elements are visible
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    const isFocused = !!(focusedPivotId && connectedNodeIds);

    // Update connection line opacity based on hover
    // In tree view: uniform width, different opacity handling
    if (viewMode === 'tree') {
      svg.selectAll('.connection-line')
        .attr('stroke-opacity', d => {
          // In focus mode, hide unconnected lines
          if (isFocused && !connectedNodeIds.has(d.nodeId)) return 0.02;
          if (hoveredNode === null) return 0.6;
          return d.nodeId === hoveredNode ? 0.9 : 0.2;
        })
        .attr('stroke-width', 1.5); // Always uniform in tree view

      svg.selectAll('.tree-connection')
        .attr('stroke-opacity', d => {
          if (isFocused && !(connectedNodeIds.has(d.sourceId) && connectedNodeIds.has(d.targetId))) return 0.02;
          if (hoveredNode === null) return 0.6;
          return (d.sourceId === hoveredNode || d.targetId === hoveredNode) ? 0.9 : 0.2;
        })
        .attr('stroke-width', 1.5); // Always uniform in tree view

      // Highlight cluster connections when hovering pivot point
      svg.selectAll('.cluster-connection')
        .attr('stroke-opacity', d => {
          if (isFocused && d.sourceId !== focusedPivotId) return 0.02;
          if (hoveredNode === null) return isFocused ? 0.8 : 0.4;
          // Highlight if hovering the cluster (source) or a member (target)
          return (d.sourceId === hoveredNode || d.targetId === hoveredNode) ? 1 : 0.1;
        })
        .attr('stroke-width', d => {
          if (isFocused && d.sourceId !== focusedPivotId) return 1;
          if (hoveredNode === null) return isFocused ? 2 : 1;
          return (d.sourceId === hoveredNode || d.targetId === hoveredNode) ? 3 : 1;
        })
        .attr('stroke-dasharray', d => {
          if (isFocused && d.sourceId === focusedPivotId) return 'none';
          if (hoveredNode === null) return '4,4';
          // Solid line when highlighted, dashed otherwise
          return (d.sourceId === hoveredNode || d.targetId === hoveredNode) ? 'none' : '4,4';
        });
    } else {
      svg.selectAll('.connection-line')
        .attr('stroke-opacity', d => {
          if (hoveredNode === null) {
            return d.source.isPivot ? 0.4 : 0.15;
          }
          return d.nodeId === hoveredNode ? 0.9 : 0.05;
        })
        .attr('stroke-width', d => {
          if (hoveredNode === null) {
            return d.source.isPivot ? 2 : 1;
          }
          return d.nodeId === hoveredNode ? 3 : 1;
        });
    }
  }, [hoveredNode, viewMode, focusedPivotId, connectedNodeIds]);

  // Focus mode animation: fade out unconnected nodes, show labels, zoom to fit
  useEffect(() => {
    if (!svgRef.current || viewMode !== 'tree') return;
    const svg = d3.select(svgRef.current);
    const nodesData = nodesDataRef.current;
    if (!nodesData || nodesData.length === 0) return;

    if (focusedPivotId && connectedNodeIds) {
      // === FOCUS IN ===
      // Save current zoom transform for restore
      preFocusTransformRef.current = zoomTransformRef.current;

      const focusedPivot = nodesData.find(n => n.id === focusedPivotId);

      // Phase 1: Fade out unconnected nodes + connections (0-400ms)
      svg.selectAll('.pivot-blob-g, .pivot-label-g')
        .transition('focus-fade')
        .duration(400)
        .style('opacity', function(d) {
          return connectedNodeIds.has(d.id) ? 1 : 0.06;
        })
        .style('pointer-events', function(d) {
          // Only blobs receive pointer events in normal mode; keep labels non-interactive
          if (!d3.select(this).classed('pivot-blob-g')) return 'none';
          return connectedNodeIds.has(d.id) ? 'all' : 'none';
        });

      // Fade unconnected connection lines
      svg.selectAll('.connection-line')
        .transition('focus-fade')
        .duration(400)
        .attr('stroke-opacity', function(d) {
          return connectedNodeIds.has(d.nodeId) ? 0.6 : 0.02;
        });

      svg.selectAll('.tree-connection')
        .transition('focus-fade')
        .duration(400)
        .attr('stroke-opacity', function(d) {
          return (connectedNodeIds.has(d.sourceId) && connectedNodeIds.has(d.targetId)) ? 0.7 : 0.02;
        });

      svg.selectAll('.cluster-connection')
        .transition('focus-fade')
        .duration(400)
        .attr('stroke-opacity', function(d) {
          return (d.sourceId === focusedPivotId) ? 0.8 : 0.02;
        })
        .attr('stroke-width', function(d) {
          return (d.sourceId === focusedPivotId) ? 2 : 1;
        })
        .attr('stroke-dasharray', function(d) {
          return (d.sourceId === focusedPivotId) ? 'none' : '4,4';
        });

      // Dim unconnected edge variables
      svg.selectAll('.scenario-edge circle')
        .transition('focus-fade')
        .duration(400)
        .attr('fill-opacity', function() {
          // Check if this variable's ID is in the connected set
          // We access the data-id attribute we'll need to add, or use position matching
          return 0.15; // Default dim - we'll enhance connected ones below
        });

      // Phase 2: Show text labels on connected nodes (200-600ms)
      const zoomableContent = svg.select('.zoomable-content');
      // Remove any previous focus labels
      zoomableContent.selectAll('.focus-label').remove();

      const connectedNodes = nodesData.filter(d =>
        connectedNodeIds.has(d.id) && d.id !== focusedPivotId && !d.isClusterOverlay
      );

      // Run a mini repulsion simulation to spread connected nodes so labels don't overlap
      // Each node needs ~60px vertical space for label below it
      const LABEL_REPULSION_RADIUS = 55;
      const focusNodes = connectedNodes.map(d => ({ id: d.id, x: d.x, y: d.y, origX: d.x, origY: d.y, radius: d.radius }));
      // Also include the focused pivot
      if (focusedPivot) {
        focusNodes.push({ id: focusedPivot.id, x: focusedPivot.x, y: focusedPivot.y, origX: focusedPivot.x, origY: focusedPivot.y, radius: focusedPivot.radius, isPivot: true });
      }

      const focusSim = d3.forceSimulation(focusNodes)
        .force('collide', d3.forceCollide().radius(LABEL_REPULSION_RADIUS).strength(0.8).iterations(4))
        .force('x', d3.forceX(d => d.origX).strength(0.3))
        .force('y', d3.forceY(d => d.origY).strength(0.3))
        .stop();

      // Run 80 ticks synchronously
      for (let i = 0; i < 80; i++) focusSim.tick();

      // Build lookup of new positions
      const spreadPositions = new Map(focusNodes.map(n => [n.id, { x: n.x, y: n.y }]));

      // Animate nodes to their spread positions
      svg.selectAll('.pivot-blob-g, .pivot-label-g')
        .filter(d => connectedNodeIds.has(d.id))
        .transition('focus-spread')
        .delay(200)
        .duration(600)
        .attr('transform', d => {
          const sp = spreadPositions.get(d.id);
          if (sp) { d.x = sp.x; d.y = sp.y; }
          return `translate(${d.x}, ${d.y})`;
        });

      // Update connection lines to follow spread positions (after animation)
      setTimeout(() => {
        const nodeDataById = new Map();
        nodesData.forEach(n => nodeDataById.set(n.id, n));
        svg.selectAll('.tree-connection')
          .transition('focus-lines')
          .duration(300)
          .attr('x1', d => nodeDataById.get(d.sourceId)?.x || 0)
          .attr('y1', d => nodeDataById.get(d.sourceId)?.y || 0)
          .attr('x2', d => nodeDataById.get(d.targetId)?.x || 0)
          .attr('y2', d => nodeDataById.get(d.targetId)?.y || 0);
        svg.selectAll('.cluster-connection')
          .transition('focus-lines')
          .duration(300)
          .attr('x1', d => nodeDataById.get(d.sourceId)?.x || 0)
          .attr('y1', d => nodeDataById.get(d.sourceId)?.y || 0)
          .attr('x2', d => nodeDataById.get(d.targetId)?.x || 0)
          .attr('y2', d => nodeDataById.get(d.targetId)?.y || 0);
      }, 500);

      // Create labels at the spread positions (append last so focus captions sit on top)
      const labelGroup = zoomableContent;
      connectedNodes.forEach((d, i) => {
        const sp = spreadPositions.get(d.id) || { x: d.x, y: d.y };
        const label = labelGroup.append('g')
          .attr('class', 'focus-label')
          .attr('data-pos', `${sp.x},${sp.y}`)
          .attr('transform', `translate(${sp.x}, ${sp.y})`)
          .style('opacity', 0)
          .style('pointer-events', 'none');

        // Wrap text into multiple lines (max 3, ellipsis if longer)
        const text = d.text || '';
        // Post-it style labels for pivot connection votes need to be readable on touch.
        const fontSize = 10;
        const maxCharsPerLine = 20;
        const maxLines = 3;
        const lineHeight = 13;
        const words = text.split(/\s+/);
        const lines = [];
        let currentLine = '';
        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;
          if (testLine.length > maxCharsPerLine && currentLine) {
            lines.push(currentLine);
            currentLine = word;
            if (lines.length >= maxLines) break;
          } else {
            currentLine = testLine;
          }
        }
        if (currentLine && lines.length < maxLines) lines.push(currentLine);
        if (lines.length === maxLines && words.length > lines.join(' ').split(/\s+/).length) {
          const last = lines[maxLines - 1];
          lines[maxLines - 1] = (last.length > maxCharsPerLine - 3 ? last.slice(0, maxCharsPerLine - 3) : last) + '...';
        }

        const maxLineWidth = Math.max(...lines.map(l => l.length)) * 5.5;
        const totalHeight = lines.length * lineHeight + 4;

        label.append('rect')
          .attr('x', -maxLineWidth / 2 - 4)
          .attr('y', d.radius + 6)
          .attr('width', maxLineWidth + 8)
          .attr('height', totalHeight)
          .attr('rx', 3)
          .attr('fill', 'rgba(0, 0, 0, 0.75)')
          .attr('stroke', 'rgba(255, 255, 255, 0.15)')
          .attr('stroke-width', 0.5);

        const textEl = label.append('text')
          .attr('text-anchor', 'middle')
          .attr('fill', 'rgba(255, 255, 255, 0.9)')
          .attr('font-size', fontSize + 'px');

        lines.forEach((line, li) => {
          textEl.append('tspan')
            .attr('x', 0)
            .attr('dy', li === 0 ? (d.radius + 18) : lineHeight)
            .text(line);
        });

        label.transition('focus-labels')
          .delay(200 + i * 30)
          .duration(400)
          .style('opacity', 1);
      });

      // Phase 3: Expand pivot circle (300-700ms)
      if (focusedPivot) {
        svg.selectAll('.pivot-blob-g')
          .filter(d => d.id === focusedPivotId)
          .select('circle')
          .transition('focus-expand')
          .delay(300)
          .duration(400)
          .attr('r', focusedPivot.radius * 1.5)
          .attr('stroke', '#9C27B0')
          .attr('stroke-width', 3);

        // Add description below pivot
        const pivotData = nodes.find(n => n.id === focusedPivotId);
        if (pivotData?.description) {
          const descGroup = labelGroup.append('g')
            .attr('class', 'focus-label')
            .attr('data-pos', `${focusedPivot.x},${focusedPivot.y}`)
            .attr('transform', `translate(${focusedPivot.x}, ${focusedPivot.y})`)
            .style('opacity', 0);

          const descText = pivotData.description.length > 80
            ? pivotData.description.slice(0, 78) + '...'
            : pivotData.description;
          const descWidth = Math.min(descText.length * 5, 220);

          descGroup.append('rect')
            .attr('x', -descWidth / 2 - 6)
            .attr('y', focusedPivot.radius * 1.5 + 30)
            .attr('width', descWidth + 12)
            .attr('height', 20)
            .attr('rx', 4)
            .attr('fill', 'rgba(156, 39, 176, 0.25)')
            .attr('stroke', 'rgba(156, 39, 176, 0.4)')
            .attr('stroke-width', 0.5);

          descGroup.append('text')
            .attr('text-anchor', 'middle')
            .attr('y', focusedPivot.radius * 1.5 + 44)
            .attr('fill', 'rgba(255, 255, 255, 0.8)')
            .attr('font-size', '10px')
            .attr('font-style', 'italic')
            .text(descText);

          descGroup.transition('focus-desc')
            .delay(400)
            .duration(400)
            .style('opacity', 1);
        }
      }

      // Phase 4: Zoom to fit connected nodes (400-1000ms)
      const connectedWithPivot = nodesData.filter(d => connectedNodeIds.has(d.id));
      if (connectedWithPivot.length > 0 && zoomRef.current) {
        const margin = 140; // Extra space for labels
        const xs = connectedWithPivot.map(d => d.x);
        const ys = connectedWithPivot.map(d => d.y);
        const minX = Math.min(...xs) - margin;
        const maxX = Math.max(...xs) + margin;
        const minY = Math.min(...ys) - margin;
        const maxY = Math.max(...ys) + margin;

        const bboxWidth = maxX - minX;
        const bboxHeight = maxY - minY;
        const rawScale = Math.min(width / bboxWidth, height / bboxHeight, 2);
        const scale = Math.max(0.3, Math.min(rawScale, 2));
        if (!Number.isFinite(scale)) return;
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;
        const translateX = width / 2 - centerX * scale;
        const translateY = height / 2 - centerY * scale;
        if (![translateX, translateY].every(Number.isFinite)) return;

        const targetTransform = d3.zoomIdentity.translate(translateX, translateY).scale(scale);

        cancelZoomTransitions(svg);
        svg.transition('focus-zoom')
          .delay(400)
          .duration(600)
          .call(zoomRef.current.transform, targetTransform);
      }
    } else {
      // === UNFOCUS (reverse) ===
      // Phase 1: Remove focus labels
      svg.select('.zoomable-content').selectAll('.focus-label')
        .transition('unfocus-labels')
        .duration(200)
        .style('opacity', 0)
        .remove();

      // Phase 2: Fade all nodes back to full opacity (0-400ms)
      svg.selectAll('.pivot-blob-g, .pivot-label-g')
        .transition('unfocus-fade')
        .duration(400)
        .style('opacity', 1)
        .style('pointer-events', function() {
          return d3.select(this).classed('pivot-blob-g') ? 'all' : 'none';
        });

      // Restore connection opacities
      svg.selectAll('.connection-line')
        .transition('unfocus-fade')
        .duration(400)
        .attr('stroke-opacity', 0.6);

      svg.selectAll('.tree-connection')
        .transition('unfocus-fade')
        .duration(400)
        .attr('stroke-opacity', 0.6);

      svg.selectAll('.cluster-connection')
        .transition('unfocus-fade')
        .duration(400)
        .attr('stroke-opacity', 0.4)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '4,4');

      // Phase 3: Shrink pivot radius back to normal
      svg.selectAll('.pivot-blob-g')
        .each(function(d) {
          d3.select(this).select('circle')
            .transition('unfocus-shrink')
            .duration(300)
            .attr('r', d.radius)
            .attr('stroke', d.isPivot ? 'rgba(255, 255, 255, 0.5)' : 'none')
            .attr('stroke-width', 2);
        });

      // Restore edge variable opacity
      svg.selectAll('.scenario-edge circle')
        .transition('unfocus-fade')
        .duration(400)
        .attr('fill-opacity', 0.8);

      // Phase 4: Zoom back to previous or identity (100-600ms)
      if (zoomRef.current) {
        const restoreTransform = preFocusTransformRef.current || d3.zoomIdentity;
        cancelZoomTransitions(svg);
        svg.transition('unfocus-zoom')
          .delay(100)
          .duration(500)
          .call(zoomRef.current.transform, restoreTransform);
        preFocusTransformRef.current = null;
      }
    }
  }, [focusedPivotId, connectedNodeIds, viewMode, width, height, nodes, scenarios]);

  // Escape key handler for unfocusing
  useEffect(() => {
    if (!focusedPivotId || !onFocusClear) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onFocusClear();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focusedPivotId, onFocusClear]);

  // Helper function to update connection lines when zoom changes
  // The zoomable-content group transform moves nodes, but connection targets need updating
  // Uses viewModeRef.current to get current viewMode (zoom handler is set up once with empty deps)
  const updateConnectionsOnZoom = (transform) => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    const currentViewMode = viewModeRef.current;

    if (currentViewMode === 'tree') {
      // Tree view: straight lines (line elements)
      svg.selectAll('.connection-line')
        .attr('x2', d => transform.invertX(d.target.x))
        .attr('y2', d => transform.invertY(d.target.y));
    } else {
      // Other views: curved lines (path elements)
      svg.selectAll('.connection-line')
        .attr('d', d => {
          // Source (node) uses raw simulation coordinates (group transform handles the rest)
          const sourceX = d.source.x;
          const sourceY = d.source.y;
          // Target (edge) needs inverse transform to appear at fixed screen position
          const targetX = transform.invertX(d.target.x);
          const targetY = transform.invertY(d.target.y);

          const midX = (sourceX + targetX) / 2;
          const midY = (sourceY + targetY) / 2;
          const dx = targetX - sourceX;
          const dy = targetY - sourceY;
          const len = Math.sqrt(dx * dx + dy * dy) || 1;

          // Spread multiple lines perpendicular to line direction
          let spreadOffset = 0;
          if (d.lineIndex !== undefined && d.totalLines > 1) {
            const centerIndex = (d.totalLines - 1) / 2;
            spreadOffset = (d.lineIndex - centerIndex) * 4;
          }

          const perpX = -dy / len;
          const perpY = dx / len;

          const cx = midX - dy * 0.1 + perpX * spreadOffset;
          const cy = midY + dx * 0.1 + perpY * spreadOffset;
          return `M ${sourceX} ${sourceY} Q ${cx} ${cy} ${targetX} ${targetY}`;
        });
    }
  };

  // Setup zoom behavior - transforms zoomable-content group, edges stay fixed
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    const zoomableContent = svg.select('.zoomable-content');

    const zoom = d3.zoom()
      .scaleExtent([0.3, 6])
      .on('zoom', (event) => {
        // Store transform for tick updates
        zoomTransformRef.current = event.transform;
        setZoomLevel(event.transform.k);
        // Transform the entire zoomable-content group (nodes move with it)
        zoomableContent.attr('transform', event.transform);
        // Update connection line targets so they stay connected to fixed edges
        updateConnectionsOnZoom(event.transform);
        // Inverse-scale focus labels so text stays readable at any zoom
        const k = event.transform.k;
        zoomableContent.selectAll('.focus-label')
          .each(function() {
            const el = d3.select(this);
            const t = el.attr('data-pos');
            if (t) {
              const [tx, ty] = t.split(',');
              el.attr('transform', `translate(${tx}, ${ty}) scale(${1/k})`);
            }
          });
      });

    // Apply zoom to SVG
    svg.call(zoom);

    // Store zoom behavior for button controls
    zoomRef.current = zoom;

    // Disable double-click zoom
    svg.on('dblclick.zoom', null);

    return () => {
      svg.on('.zoom', null);
    };
  }, []);

  // Zoom control handlers
  const handleZoomIn = () => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    cancelZoomTransitions(svg);
    svg.call(zoomRef.current.scaleBy, 1.3);
  };

  const handleZoomOut = () => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    cancelZoomTransitions(svg);
    svg.call(zoomRef.current.scaleBy, 0.7);
  };

  const handleZoomReset = () => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    cancelZoomTransitions(svg);
    svg.call(zoomRef.current.transform, d3.zoomIdentity);
  };

  // Render scenario edges and variables
  const renderEdges = () => {
    const titlePadding = width < 720 ? 26 : 20; // Slightly inset on narrow viewports
    const variablePadding = width < 720 ? 50 : 45; // Padding from edge for variables (after title)
    const iconSize = 20; // Size of SVG icons
    const scenarioTitleMax = width < 640 ? 16 : width < 1000 ? 24 : 34;

    return scenarios.map((scenario, idx) => {
      const edge = EDGES[idx % 4];

      // Calculate title position and rotation based on edge
      let titleX, titleY, titleRotation, titleAnchor;
      if (edge === 'top') {
        titleX = width / 2;
        titleY = titlePadding;
        titleRotation = 0;
        titleAnchor = 'middle';
      } else if (edge === 'bottom') {
        titleX = width / 2;
        titleY = height - titlePadding;
        titleRotation = 0;
        titleAnchor = 'middle';
      } else if (edge === 'left') {
        titleX = titlePadding;
        titleY = height / 2;
        titleRotation = -90;
        titleAnchor = 'middle';
      } else { // right
        titleX = width - titlePadding;
        titleY = height / 2;
        titleRotation = 90;
        titleAnchor = 'middle';
      }

      // Get SVG icon path for this scenario
      const iconPath = SCENARIO_ICONS[scenario.title];

      // Calculate icon position (offset from text center)
      const iconOffset = 50; // Distance from center for icon
      let iconX, iconY;
      if (edge === 'top' || edge === 'bottom') {
        iconX = titleX - iconOffset - iconSize / 2;
        iconY = titleY - iconSize / 2;
      } else if (edge === 'left') {
        // Rotated -90°, so Y becomes the "horizontal" offset
        iconX = titleX - iconSize / 2;
        iconY = titleY - iconOffset - iconSize / 2;
      } else { // right
        // Rotated 90°, so Y offset is reversed
        iconX = titleX - iconSize / 2;
        iconY = titleY + iconOffset - iconSize / 2;
      }

      // When focused, dim scenario titles if none of their variables are connected
      const hasConnectedVar = !connectedVariableIds || scenario.variables?.some(v => connectedVariableIds.has(v.id));
      const scenarioTitleOpacity = hasConnectedVar ? 0.9 : 0.1;

      return (
        <g key={scenario.id} className="scenario-edge">
          {/* Scenario icon */}
          {iconPath && (
            <image
              href={iconPath}
              x={iconX}
              y={iconY}
              width={iconSize}
              height={iconSize}
              transform={titleRotation !== 0 ? `rotate(${titleRotation}, ${titleX}, ${titleY})` : undefined}
              style={{ filter: `drop-shadow(0 0 4px ${scenario.color || '#fff'})`, transition: 'opacity 0.4s ease', opacity: scenarioTitleOpacity }}
            />
          )}
          {/* Scenario label - rotated for side edges */}
          <text
            x={titleX}
            y={titleY}
            textAnchor={titleAnchor}
            dominantBaseline="middle"
            transform={titleRotation !== 0 ? `rotate(${titleRotation}, ${titleX}, ${titleY})` : undefined}
            fill={scenario.color || 'white'}
            fontSize="14"
            fontWeight="700"
            opacity={scenarioTitleOpacity}
            style={{ transition: 'opacity 0.4s ease' }}
          >
            {(scenario.title || '').length > scenarioTitleMax
              ? `${(scenario.title || '').slice(0, scenarioTitleMax - 1)}…`
              : (scenario.title || '')}
          </text>

          {/* Variable markers - positioned with padding after title */}
          {scenario.variables.map((variable, varIdx) => {
            const pos = variablePositions[variable.id];
            if (!pos) return null;

            // Adjust variable circle position based on edge
            let circleX = pos.x;
            let circleY = pos.y;
            if (edge === 'top') {
              circleY = variablePadding;
            } else if (edge === 'bottom') {
              circleY = height - variablePadding;
            } else if (edge === 'left') {
              circleX = variablePadding;
            } else { // right
              circleX = width - variablePadding;
            }

            // Labels sit outside the circle (toward the screen edge), tilted on
            // top/bottom, with alternating offset so consecutive vars don't stack.
            const altSign = varIdx % 2 === 0 ? 1 : -1;
            const alongEdge = altSign * 10;
            const outBase = 16;
            const outExtra = varIdx % 2 === 0 ? 0 : 8;
            const out = outBase + outExtra;

            let labelX, labelY, labelRotation, labelAnchor;
            if (edge === 'top') {
              labelX = circleX + alongEdge;
              labelY = circleY - out;
              labelRotation = -30;
              labelAnchor = 'middle';
            } else if (edge === 'bottom') {
              labelX = circleX + alongEdge;
              labelY = circleY + out;
              labelRotation = -30;
              labelAnchor = 'middle';
            } else if (edge === 'left') {
              labelX = circleX - out;
              labelY = circleY + alongEdge;
              labelRotation = -90;
              labelAnchor = 'middle';
            } else { // right
              labelX = circleX + out;
              labelY = circleY + alongEdge;
              labelRotation = 90;
              labelAnchor = 'middle';
            }

            // Split long titles into 2 lines; keep lines short on small layouts to avoid overlap
            const title = variable.title || '';
            const maxCharsPerLine = width < 640 ? 12 : width < 1000 ? 16 : 18;
            const maxTotal = maxCharsPerLine * 2 + 4;
            const t = title.length > maxTotal ? `${title.slice(0, maxTotal - 1)}…` : title;
            let lines = [t];
            if (t.length > maxCharsPerLine) {
              const midpoint = Math.floor(t.length / 2);
              let breakIndex = t.lastIndexOf(' ', midpoint + 5);
              if (breakIndex < midpoint - 8) {
                breakIndex = t.indexOf(' ', midpoint - 5);
              }
              if (breakIndex > 0 && breakIndex < t.length - 3) {
                lines = [t.slice(0, breakIndex), t.slice(breakIndex + 1)];
              }
            }

            // Adjust line spacing based on edge orientation
            const lineSpacing = (edge === 'left' || edge === 'right') ? 10 : 12;

            // When focused, dim unconnected edge variables
            const isVarConnected = !connectedVariableIds || connectedVariableIds.has(variable.id);
            const varOpacity = isVarConnected ? 1 : 0.08;

            return (
              <g key={variable.id} style={{ transition: 'opacity 0.4s ease', opacity: varOpacity }}>
                <circle
                  cx={circleX}
                  cy={circleY}
                  r={6}
                  fill={scenario.color || '#666'}
                  fillOpacity={0.8}
                  stroke="rgba(255, 255, 255, 0.3)"
                  strokeWidth={1}
                />
                <text
                  x={labelX}
                  y={labelY}
                  textAnchor={labelAnchor}
                  dominantBaseline="middle"
                  transform={`rotate(${labelRotation}, ${labelX}, ${labelY})`}
                  fill="rgba(255, 255, 255, 0.7)"
                  fontSize="10"
                >
                  {lines.map((line, i) => (
                    <tspan
                      key={i}
                      x={labelX}
                      dy={i === 0 ? (lines.length > 1 ? -lineSpacing / 2 : 0) : lineSpacing}
                    >
                      {line}
                    </tspan>
                  ))}
                </text>
              </g>
            );
          })}
        </g>
      );
    });
  };

  return (
    <Container>
      <SVGContainer ref={svgRef} viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>

          {/* Arrow marker for tree connections */}
          <marker
            id="tree-arrow"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="rgba(255, 255, 255, 0.7)" />
          </marker>

          {/* Dynamic gradients for multi-scenario nodes */}
          {nodeGradients.map(gradient => {
            // Edge positions in the gradient (0-100% coordinates)
            // Each color should appear on its edge side
            const edgePositions = {
              top: { x: 50, y: 0 },      // Top center
              right: { x: 100, y: 50 },  // Right center
              bottom: { x: 50, y: 100 }, // Bottom center
              left: { x: 0, y: 50 }      // Left center
            };

            // For 2 scenarios: linear gradient from one edge toward the other
            if (gradient.scenarios.length === 2) {
              const [s1, s2] = gradient.scenarios;
              const p1 = edgePositions[s1.edge];
              const p2 = edgePositions[s2.edge];

              return (
                <linearGradient
                  key={gradient.id}
                  id={gradient.id}
                  x1={`${p1.x}%`}
                  y1={`${p1.y}%`}
                  x2={`${p2.x}%`}
                  y2={`${p2.y}%`}
                >
                  <stop offset="0%" stopColor={s1.color} />
                  <stop offset="100%" stopColor={s2.color} />
                </linearGradient>
              );
            }

            // For 3-4 scenarios: create a diamond pattern with 4 directional gradients
            // Each gradient points from center toward its edge
            const scenarios = gradient.scenarios;

            // Create color map for each edge
            // For missing edges (3-color case), blend neighboring colors
            const edgeColors = {
              top: null,
              right: null,
              bottom: null,
              left: null
            };
            scenarios.forEach(s => {
              edgeColors[s.edge] = s.color;
            });

            // For 3-color case: fill missing edge with blend of neighbors
            const edges = ['top', 'right', 'bottom', 'left'];
            edges.forEach((edge, i) => {
              if (!edgeColors[edge]) {
                // Get neighboring edges (wrap around)
                const prevEdge = edges[(i + 3) % 4];
                const nextEdge = edges[(i + 1) % 4];
                const prevColor = edgeColors[prevEdge];
                const nextColor = edgeColors[nextEdge];
                // Use whichever neighbor has a color, or blend if both exist
                if (prevColor && nextColor) {
                  // Both neighbors have colors - use a subtle blend (just pick one for simplicity)
                  edgeColors[edge] = prevColor;
                } else {
                  edgeColors[edge] = prevColor || nextColor || '#666';
                }
              }
            });

            // Create 4 radial gradients, each pointing toward one edge
            // Then use clipPath to show only the quadrant facing that edge
            return (
              <React.Fragment key={gradient.id}>
                {/* Clip paths for each quadrant */}
                <clipPath id={`${gradient.id}-clip-top`}>
                  <polygon points="50,50 0,0 100,0" />
                </clipPath>
                <clipPath id={`${gradient.id}-clip-right`}>
                  <polygon points="50,50 100,0 100,100" />
                </clipPath>
                <clipPath id={`${gradient.id}-clip-bottom`}>
                  <polygon points="50,50 100,100 0,100" />
                </clipPath>
                <clipPath id={`${gradient.id}-clip-left`}>
                  <polygon points="50,50 0,100 0,0" />
                </clipPath>

                {/* Blur filter for soft transitions - more feathering */}
                <filter id={`${gradient.id}-blur`} x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="8" />
                </filter>

                {/* Radial gradients from center toward each edge */}
                <radialGradient id={`${gradient.id}-top`} cx="50%" cy="100%" r="100%">
                  <stop offset="0%" stopColor={edgeColors.top} stopOpacity="0.4" />
                  <stop offset="60%" stopColor={edgeColors.top} stopOpacity="0.8" />
                  <stop offset="100%" stopColor={edgeColors.top} />
                </radialGradient>
                <radialGradient id={`${gradient.id}-right`} cx="0%" cy="50%" r="100%">
                  <stop offset="0%" stopColor={edgeColors.right} stopOpacity="0.4" />
                  <stop offset="60%" stopColor={edgeColors.right} stopOpacity="0.8" />
                  <stop offset="100%" stopColor={edgeColors.right} />
                </radialGradient>
                <radialGradient id={`${gradient.id}-bottom`} cx="50%" cy="0%" r="100%">
                  <stop offset="0%" stopColor={edgeColors.bottom} stopOpacity="0.4" />
                  <stop offset="60%" stopColor={edgeColors.bottom} stopOpacity="0.8" />
                  <stop offset="100%" stopColor={edgeColors.bottom} />
                </radialGradient>
                <radialGradient id={`${gradient.id}-left`} cx="100%" cy="50%" r="100%">
                  <stop offset="0%" stopColor={edgeColors.left} stopOpacity="0.4" />
                  <stop offset="60%" stopColor={edgeColors.left} stopOpacity="0.8" />
                  <stop offset="100%" stopColor={edgeColors.left} />
                </radialGradient>
              </React.Fragment>
            );
          })}
        </defs>

        {/* Edges and variables - NOT zoomable, stays fixed */}
        <g className="edges-layer">
          {renderEdges()}
        </g>

        {/* Zoomable content - contains nodes and part of connection lines */}
        <g className="zoomable-content">
          {/* Connection lines - source zooms with nodes, target stays at edge */}
          <g className="connections-layer" />
          {/* Nodes */}
          <g className="nodes-layer" />
        </g>
      </SVGContainer>

      {/* Back button when focused on a pivot point */}
      {focusedPivotId && onFocusClear && (
        <div style={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <button
            onClick={onFocusClear}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 16px',
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 8,
              color: 'white',
              fontSize: '0.85rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            {t('pivotPoints.spatial.back')}
          </button>
          <span style={{
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '0.75rem'
          }}>
            ESC
          </span>
        </div>
      )}

      {!isTouchDevice() && (
        <ZoomControls>
          <ZoomButton onClick={handleZoomIn} title={t('pivotPoints.common.zoomIn')}>+</ZoomButton>
          <ZoomLevel>{Math.round(zoomLevel * 100)}%</ZoomLevel>
          <ZoomButton onClick={handleZoomOut} title={t('pivotPoints.common.zoomOut')}>−</ZoomButton>
          <ZoomButton onClick={handleZoomReset} title={t('pivotPoints.common.zoomReset')} style={{ fontSize: '0.9rem' }}>⟲</ZoomButton>
        </ZoomControls>
      )}

      <Tooltip $visible={tooltip.visible && !selectedNode} $connectionCount={tooltip.node?.connectionCount || 0}>
        {tooltip.node && (
          <>
            <div className="title">{tooltip.node.text}</div>
            <div className="meta">
              {tooltip.node.participant && t('pivotPoints.common.participant', { name: tooltip.node.participant })}
              {tooltip.node.depth && ` • ${t('pivotPoints.common.level', { depth: tooltip.node.depth })}`}
            </div>
            <div className="connections">
              <ConnectionsList style={{ justifyContent: 'center', marginTop: '8px', flexWrap: 'wrap' }}>
                {/* In tree view: show connected nodes (parent + children) */}
                {/* In other views: show connected variables */}
                {(() => {
                  // TREE VIEW: Show connected nodes
                  if (viewMode === 'tree') {
                    const connectedNodes = [];

                    // For cluster overlays: show member nodes
                    if (tooltip.node.isClusterOverlay && tooltip.node.memberIds) {
                      tooltip.node.memberIds.forEach(memberId => {
                        const member = allNodes.find(n => n.id === memberId);
                        if (member) {
                          connectedNodes.push({
                            id: memberId,
                            text: member.text?.substring(0, 40) + (member.text?.length > 40 ? '...' : ''),
                            type: 'member'
                          });
                        }
                      });
                    } else {
                      // For individual nodes: show parent and children
                      // Parent
                      if (tooltip.node.parentId) {
                        const parent = allNodes.find(n => n.id === tooltip.node.parentId);
                        if (parent) {
                          connectedNodes.push({
                            id: parent.id,
                            text: parent.text?.substring(0, 40) + (parent.text?.length > 40 ? '...' : ''),
                            type: 'parent'
                          });
                        }
                      }
                      // Children
                      allNodes.filter(n => n.parentId === tooltip.node.id).forEach(child => {
                        connectedNodes.push({
                          id: child.id,
                          text: child.text?.substring(0, 40) + (child.text?.length > 40 ? '...' : ''),
                          type: 'child'
                        });
                      });
                    }

                    if (connectedNodes.length > 0) {
                      return connectedNodes.slice(0, 5).map(node => (
                        <ConnectionTag
                          key={node.id}
                          $color={node.type === 'parent' ? '#FFA726' : node.type === 'member' ? '#42A5F5' : '#66BB6A'}
                          style={{ fontSize: '10px', padding: '2px 6px' }}
                        >
                          {node.type === 'parent' ? '↑ ' : node.type === 'child' ? '↓ ' : '• '}{node.text}
                        </ConnectionTag>
                      ));
                    }
                    return <span style={{ opacity: 0.5, fontSize: '11px' }}>{t('pivotPoints.spatial.noConnections')}</span>;
                  }

                  // OTHER VIEWS: Show connected variables
                  // Prefer connectedVariableCounts for clusters (guaranteed unique keys)
                  if (tooltip.node.connectedVariableCounts && Object.keys(tooltip.node.connectedVariableCounts).length > 0) {
                    return Object.keys(tooltip.node.connectedVariableCounts).map(varId => {
                      const varInfo = variablePositions[varId];
                      return (
                        <ConnectionTag
                          key={varId}
                          $color={varInfo?.scenario?.color}
                        >
                          {varInfo?.variable?.title || varId}
                        </ConnectionTag>
                      );
                    });
                  }
                  // For individual nodes: deduplicate connectedVariables by title
                  if (tooltip.node.connectedVariables?.length > 0) {
                    const seenTitles = new Set();
                    return tooltip.node.connectedVariables
                      .filter(varId => {
                        const varInfo = variablePositions[varId];
                        const title = varInfo?.variable?.title || varId;
                        if (seenTitles.has(title)) return false;
                        seenTitles.add(title);
                        return true;
                      })
                      .map(varId => {
                        const varInfo = variablePositions[varId];
                        return (
                          <ConnectionTag
                            key={varId}
                            $color={varInfo?.scenario?.color}
                          >
                            {varInfo?.variable?.title || varId}
                          </ConnectionTag>
                        );
                      });
                  }
                  // Fallback: show scenarioNames for RAG pivot points that lack connectedVariables
                  if (tooltip.node.scenarioNames?.length > 0) {
                    return tooltip.node.scenarioNames.map(name => (
                      <ConnectionTag
                        key={name}
                        $color={resolveScenarioColor(name) || '#607d8b'}
                      >
                        {name}
                      </ConnectionTag>
                    ));
                  }
                  return null;
                })()}
              </ConnectionsList>
            </div>
          </>
        )}
      </Tooltip>

      {/* Detail Modal */}
      <ModalWrapper visible={!!selectedNode} onClose={() => setSelectedNode(null)} showCloseButton={false}>
        {selectedNode && (
          <ModalContent>
            <ModalCloseButton onClick={() => setSelectedNode(null)}>
              <X />
            </ModalCloseButton>

            <ModalTitle>{selectedNode.text}</ModalTitle>

            {/* AI-generated description/analysis */}
            <ModalSection>
              <ModalSectionTitle>{t('pivotPoints.common.analysis')}</ModalSectionTitle>
              <DescriptionText>
                {selectedNode.description || <span style={{ color: '#666', fontStyle: 'italic' }}>{t('pivotPoints.spatial.noAnalysis')}</span>}
              </DescriptionText>
              {(selectedNode.challengeSeverity || selectedNode.solutionOptimism) && (
                <SentimentGauges>
                  <GaugeContainer>
                    <GaugeLabel>{t('pivotPoints.detail.severity')}</GaugeLabel>
                    <GaugeBar>
                      <GaugeFill
                        $value={selectedNode.challengeSeverity || 3}
                        $color={selectedNode.challengeSeverity >= 4 ? '#f44336' : selectedNode.challengeSeverity >= 3 ? '#ff9800' : '#4caf50'}
                      />
                    </GaugeBar>
                    <GaugeValue $color={selectedNode.challengeSeverity >= 4 ? '#f44336' : selectedNode.challengeSeverity >= 3 ? '#ff9800' : '#4caf50'}>
                      {selectedNode.challengeSeverity || 3}/5
                    </GaugeValue>
                  </GaugeContainer>
                  <GaugeContainer>
                    <GaugeLabel>{t('pivotPoints.detail.optimism')}</GaugeLabel>
                    <GaugeBar>
                      <GaugeFill
                        $value={selectedNode.solutionOptimism || 3}
                        $color={selectedNode.solutionOptimism >= 4 ? '#4caf50' : selectedNode.solutionOptimism >= 3 ? '#ff9800' : '#f44336'}
                      />
                    </GaugeBar>
                    <GaugeValue $color={selectedNode.solutionOptimism >= 4 ? '#4caf50' : selectedNode.solutionOptimism >= 3 ? '#ff9800' : '#f44336'}>
                      {selectedNode.solutionOptimism || 3}/5
                    </GaugeValue>
                  </GaugeContainer>
                </SentimentGauges>
              )}
              {(selectedNode.leverageActor || selectedNode.leverageEffect || selectedNode.isRootCause) && (
                <div style={{ marginTop: 12, fontSize: '0.9rem', lineHeight: 1.45 }}>
                  {selectedNode.leverageActor && (
                    <p style={{ margin: '0 0 6px 0' }}>
                      <strong>{t('pivotPoints.detail.leverageActor')}:</strong>{' '}
                      {selectedNode.leverageActor}
                    </p>
                  )}
                  {selectedNode.leverageEffect && (
                    <p style={{ margin: '0 0 6px 0' }}>
                      <strong>{t('pivotPoints.detail.leverageEffect')}:</strong>{' '}
                      {selectedNode.leverageEffect}
                    </p>
                  )}
                  {selectedNode.isRootCause === true && (
                    <p style={{ margin: 0, color: '#7c3aed' }}>
                      {t('pivotPoints.detail.rootCause')}
                    </p>
                  )}
                </div>
              )}
            </ModalSection>

            {/* Scenario distribution with pie chart + node titles */}
            <ModalSection>
              <ModalSectionTitle>{t('pivotPoints.spatial.linkedVariables', { count: selectedNode.memberCount || selectedNode.size || 1 })}</ModalSectionTitle>
              {(() => {
                // Group variables by scenario with counts
                const scenarioGroups = {};

                if (selectedNode.connectedVariables?.length > 0) {
                  selectedNode.connectedVariables.forEach(varId => {
                    const varInfo = variablePositions[varId];
                    const scenarioName = varInfo?.scenario?.title || t('pivotPoints.common.unknown');
                    const scenarioColor = varInfo?.scenario?.color || SCENARIO_COLORS_BY_NAME[scenarioName] || '#607d8b';
                    const varTitle = varInfo?.variable?.title || varId;

                    if (!scenarioGroups[scenarioName]) {
                      scenarioGroups[scenarioName] = { color: scenarioColor, variables: {} };
                    }
                    if (!scenarioGroups[scenarioName].variables[varTitle]) {
                      scenarioGroups[scenarioName].variables[varTitle] = 0;
                    }
                    scenarioGroups[scenarioName].variables[varTitle]++;
                  });
                } else if (selectedNode.scenarioNames?.length > 0) {
                  selectedNode.scenarioNames.forEach(name => {
                    const scenario = scenarios.find(s => s.title === name || s.title?.toLowerCase() === name?.toLowerCase());
                    if (!scenarioGroups[name]) {
                      scenarioGroups[name] = {
                        color: scenario?.color || SCENARIO_COLORS_BY_NAME[name] || '#607d8b',
                        variables: {}
                      };
                    }
                  });
                }

                const scenarioEntries = Object.entries(scenarioGroups).sort((a, b) =>
                  Object.values(b[1].variables).reduce((s, c) => s + c, 0) -
                  Object.values(a[1].variables).reduce((s, c) => s + c, 0)
                );
                const total = scenarioEntries.reduce((sum, [, data]) =>
                  sum + Object.values(data.variables).reduce((s, c) => s + c, 0), 0
                );

                if (total === 0 && scenarioEntries.length === 0) {
                  return <ModalText>{t('pivotPoints.spatial.noScenarioInfo')}</ModalText>;
                }

                // Build pie chart data
                let startAngle = 0;
                const pieSlices = scenarioEntries.map(([name, data]) => {
                  const count = Object.values(data.variables).reduce((s, c) => s + c, 0) || 1;
                  const angle = (count / Math.max(total, 1)) * 360;
                  const slice = { name, count, color: data.color, startAngle, endAngle: startAngle + angle };
                  startAngle += angle;
                  return slice;
                });

                // SVG arc helper
                const describeArc = (cx, cy, r, startDeg, endDeg) => {
                  const startRad = (startDeg - 90) * Math.PI / 180;
                  const endRad = (endDeg - 90) * Math.PI / 180;
                  const x1 = cx + r * Math.cos(startRad);
                  const y1 = cy + r * Math.sin(startRad);
                  const x2 = cx + r * Math.cos(endRad);
                  const y2 = cy + r * Math.sin(endRad);
                  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
                  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
                };

                return (
                  <ScenarioDistribution style={{ alignItems: 'flex-start' }}>
                    {/* Mini pie chart */}
                    <svg width="50" height="50" viewBox="0 0 50 50" style={{ flexShrink: 0 }}>
                      {pieSlices.map((slice, i) => (
                        <path
                          key={i}
                          d={describeArc(25, 25, 23, slice.startAngle, slice.endAngle)}
                          fill={slice.color}
                          stroke="#0a0a0a"
                          strokeWidth="1"
                        />
                      ))}
                    </svg>

                    {/* Tags grouped by scenario */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                      {scenarioEntries.map(([scenarioName, data]) => {
                        const scenarioTotal = Object.values(data.variables).reduce((s, c) => s + c, 0);
                        return (
                          <div key={scenarioName}>
                            <div style={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: data.color,
                              marginBottom: '2px'
                            }}>
                              {scenarioName} ({scenarioTotal})
                            </div>
                            <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.4 }}>
                              {Object.entries(data.variables).sort((a, b) => b[1] - a[1]).map(([varTitle, count], i, arr) => (
                                <span key={i}>
                                  {varTitle}{count > 1 ? ` ×${count}` : ''}{i < arr.length - 1 ? ', ' : ''}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScenarioDistribution>
                );
              })()}
            </ModalSection>

            <MetaInfo>
              <MetaItem>
                {t('pivotPoints.common.typeLabel')} <span>{selectedNode.isPivot ? t('pivotPoints.common.pivotPoint') : t('pivotPoints.common.cluster')}</span>
              </MetaItem>
              {selectedNode.memberCount && (
                <MetaItem>
                  {t('pivotPoints.common.votesLabel')} <span>{selectedNode.memberCount}</span>
                </MetaItem>
              )}
            </MetaInfo>
          </ModalContent>
        )}
      </ModalWrapper>
    </Container>
  );
}

export default PivotPointsView;
